import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft, Package, ShoppingBag, Users, BarChart3, Settings, ClipboardList,
  Upload, RefreshCw, Trash2, Download, User, Coins, Clock, AlertTriangle, Store,
  Percent, Link2, Search, X, Pause, Play, ExternalLink
} from 'lucide-react';
import { API_URL } from '../config/api';
import logService from '../services/logService';
import useEscapeClose from '../hooks/useEscapeClose';
import MarketplacePanel from '../components/MarketplacePanel';
import './ShopifySettings.css';

// Auto-sync intervals (in milliseconds)
const ORDERS_POLL_INTERVAL = 30000; // 30 seconds
const PRODUCTS_POLL_INTERVAL = 60000; // 60 seconds
// Yeni nesil ilan/yayın uygulaması (akik-pazaryeri) — ayrı bir servis, buradan sadece
// yeni sekmede açılır. Alt alan adı: bkz. akik-pazaryeri/deploy/akikpazar.qubite.net.conf
const PAZARYERI_APP_URL = 'https://akikpazar.qubite.net';
const ShopifySettings = ({ user, selectedStore }) => {
  const navigate = useNavigate();
  const [activePlatform, setActivePlatform] = useState(() => {
    return localStorage.getItem('marketplace_active_platform') || 'shopify';
  });
  const [activeTab, setActiveTab] = useState('products');

  // Seçilen platformu hatırla
  useEffect(() => {
    localStorage.setItem('marketplace_active_platform', activePlatform);
  }, [activePlatform]);

  const isAdmin = user?.role === 'admin';

  // ===== TRENDYOL STATE =====
  const [trendyolSettings, setTrendyolSettings] = useState({
    seller_id: '', api_key: '', api_secret: '', integration_code: '',
    default_deduction_store_id: '', sync_enabled: false,
    commission_percentage: 0, price_change_threshold: 2, price_sync_interval: 0,
    last_synced_gold_price: null, last_synced_usd_rate: null, last_price_sync_at: null
  });
  const [trendyolProducts, setTrendyolProducts] = useState([]);
  const [trendyolOrders, setTrendyolOrders] = useState([]);
  const [trendyolCustomers, setTrendyolCustomers] = useState([]);
  const [trendyolLogs, setTrendyolLogs] = useState([]);
  const [loadingTrendyol, setLoadingTrendyol] = useState(false);
  const [trendyolConnectionStatus, setTrendyolConnectionStatus] = useState(null);
  const [trendyolSyncProgress, setTrendyolSyncProgress] = useState(null);
  const [syncingTrendyolProducts, setSyncingTrendyolProducts] = useState(false);
  const [syncingTrendyolInventory, setSyncingTrendyolInventory] = useState(false);
  const [syncingTrendyolPrices, setSyncingTrendyolPrices] = useState(false);
  const [pullingTrendyolOrders, setPullingTrendyolOrders] = useState(false);
  const [selectedTrendyolProducts, setSelectedTrendyolProducts] = useState([]);
  const [selectedTrendyolOrders, setSelectedTrendyolOrders] = useState([]);
  const [trendyolOrderFilter, setTrendyolOrderFilter] = useState('all');
  const [trendyolNewOrderCount, setTrendyolNewOrderCount] = useState(0);

  // Auto-sync state
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);
  const [lastOrderSync, setLastOrderSync] = useState(null);
  const [lastProductSync, setLastProductSync] = useState(null);
  const [isLive, setIsLive] = useState(true);
  const ordersIntervalRef = useRef(null);
  const productsIntervalRef = useRef(null);

  // Settings state
  const [settings, setSettings] = useState({
    shop_url: '',
    access_token: '',
    api_version: '2024-01',
    webhook_secret: '',
    default_deduction_store_id: '',
    sync_enabled: false,
    store_currency: 'USD',
    exchange_rate_try_to_usd: 0.0286,
    commission_percentage: 0,
    auto_publish: false,
    price_change_threshold: 2,
    price_sync_interval: 0,
    last_synced_gold_price: null,
    last_synced_usd_rate: null,
    last_price_sync_at: null
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // OAuth state
  const [oauthStatus, setOauthStatus] = useState({ connected: false, loading: true });
  const [connectingOAuth, setConnectingOAuth] = useState(false);
  const [disconnectingOAuth, setDisconnectingOAuth] = useState(false);

  // Stores
  const [stores, setStores] = useState([]);

  // Products state
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [syncingProducts, setSyncingProducts] = useState(false);
  const [syncingInventory, setSyncingInventory] = useState(false);
  const [syncingPrices, setSyncingPrices] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [productFilter, setProductFilter] = useState('');
  const [imagePopup, setImagePopup] = useState({ show: false, src: '', name: '' });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Orders state
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [pullingOrders, setPullingOrders] = useState(false);
  const [orderFilter, setOrderFilter] = useState('all');
  const [newOrderCount, setNewOrderCount] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkDeletingOrders, setBulkDeletingOrders] = useState(false);

  // Customers state
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loadingCustomerDetail, setLoadingCustomerDetail] = useState(false);

  // Logs state
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logFilter, setLogFilter] = useState({ type: '', status: '' });

  // Multi-location state
  const [shopifyLocations, setShopifyLocations] = useState([]);
  const [storeLocationMappings, setStoreLocationMappings] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [savingMappings, setSavingMappings] = useState(false);
  const [syncingMultiLocation, setSyncingMultiLocation] = useState(false);

  // Modal kapanış animasyonu için
  const [closingModal, setClosingModal] = useState(null);

  // Genel onay modalı (window.confirm yerine)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Evet',
    cancelText: 'İptal',
    type: 'warning',
    onConfirm: () => {}
  });

  // Genel onay modal'ı gösterme fonksiyonu
  const showConfirmation = (config) => {
    setConfirmModalConfig({
      title: config.title || 'Onay',
      message: config.message || '',
      confirmText: config.confirmText || 'Evet',
      cancelText: config.cancelText || 'İptal',
      onConfirm: config.onConfirm || (() => {}),
      type: config.type || 'warning'
    });
    setShowConfirmModal(true);
  };

  // ESC tuşu ile modal kapatma
  useEscapeClose([
    [showConfirmModal, () => setShowConfirmModal(false)],
    [imagePopup.show, () => setImagePopup({ show: false, src: '', name: '' })],
  ]);

  const tabs = [
    { id: 'products', name: 'Ürünler', icon: <Package size={18} /> },
    { id: 'orders', name: 'Siparişler', icon: <ShoppingBag size={18} /> },
    { id: 'customers', name: 'Müşteriler', icon: <Users size={18} /> },
    { id: 'settings', name: 'Ayarlar', icon: <Settings size={18} /> },
    { id: 'logs', name: 'Sync Logları', icon: <ClipboardList size={18} /> }
  ];

  // ===== SHOPIFY İNDİRİM & ADRES (HANDLE) STATE =====
  const [discountSubTab, setDiscountSubTab] = useState('campaigns'); // 'campaigns' | 'handles'
  const [discountCampaigns, setDiscountCampaigns] = useState([]);
  const [loadingDiscounts, setLoadingDiscounts] = useState(false);
  const [productCategories, setProductCategories] = useState([]);
  const [productBrands, setProductBrands] = useState([]);
  const [shopifyCollections, setShopifyCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [pushStatus, setPushStatus] = useState(null);
  const pushStatusPollRef = useRef(null);
  const emptyDiscountForm = {
    name: '', scopeType: 'all', category: '', brand: '', collectionId: '', collectionTitle: '',
    discountType: 'percent', discountValue: '', rounding: 'none'
  };
  const [discountForm, setDiscountForm] = useState(emptyDiscountForm);
  const [discountProductSearch, setDiscountProductSearch] = useState('');
  const [discountProductResults, setDiscountProductResults] = useState([]);
  const [discountSelectedProducts, setDiscountSelectedProducts] = useState([]); // [{id,name,barcode}]
  const [discountPreview, setDiscountPreview] = useState(null);
  const [previewingDiscount, setPreviewingDiscount] = useState(false);
  const [applyingDiscount, setApplyingDiscount] = useState(false);

  const [handleScopeType, setHandleScopeType] = useState('all');
  const [handleCategory, setHandleCategory] = useState('');
  const [handleBrand, setHandleBrand] = useState('');
  const [handleProductSearch, setHandleProductSearch] = useState('');
  const [handleProductResults, setHandleProductResults] = useState([]);
  const [handleSelectedProducts, setHandleSelectedProducts] = useState([]);
  const [handleRedirect, setHandleRedirect] = useState(true);
  const [handlePreview, setHandlePreview] = useState(null);
  const [previewingHandle, setPreviewingHandle] = useState(false);
  const [applyingHandle, setApplyingHandle] = useState(false);

  const buildScopePayload = (scopeType, category, brand, selectedProducts, collectionId, collectionTitle) => {
    if (scopeType === 'category') return { type: 'category', category };
    if (scopeType === 'brand') return { type: 'brand', brand };
    if (scopeType === 'products') return { type: 'products', product_ids: selectedProducts.map(p => p.id) };
    if (scopeType === 'shopify_collection') return { type: 'shopify_collection', collection_id: collectionId, collection_title: collectionTitle };
    return { type: 'all' };
  };

  const fetchDiscountCampaigns = async () => {
    setLoadingDiscounts(true);
    try {
      const res = await fetch(`${API_URL}/shopify/discounts`);
      const data = await res.json();
      setDiscountCampaigns(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('İndirim kampanyaları alınamadı:', err);
    } finally {
      setLoadingDiscounts(false);
    }
  };

  const fetchCategoriesAndBrands = async () => {
    try {
      const [catRes, brandRes] = await Promise.all([
        fetch(`${API_URL}/categories`),
        fetch(`${API_URL}/brands`)
      ]);
      setProductCategories(await catRes.json());
      setProductBrands(await brandRes.json());
    } catch (err) {
      console.error('Kategori/marka alınamadı:', err);
    }
  };

  const fetchShopifyCollections = async () => {
    setLoadingCollections(true);
    try {
      const res = await fetch(`${API_URL}/shopify/collections`);
      const data = await res.json();
      setShopifyCollections(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Shopify koleksiyonları alınamadı:', err);
    } finally {
      setLoadingCollections(false);
    }
  };

  const fetchPushStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/shopify/discounts/push-status`);
      const data = await res.json();
      setPushStatus(data);
      if (!data.running) {
        clearInterval(pushStatusPollRef.current);
        pushStatusPollRef.current = null;
        fetchDiscountCampaigns();
      }
    } catch (err) {
      console.error('Push durumu alınamadı:', err);
    }
  };

  const startPollingPushStatus = () => {
    if (pushStatusPollRef.current) return;
    fetchPushStatus();
    pushStatusPollRef.current = setInterval(fetchPushStatus, 1500);
  };

  useEffect(() => {
    return () => { if (pushStatusPollRef.current) clearInterval(pushStatusPollRef.current); };
  }, []);

  const searchProductsForPicker = async (query, setResults) => {
    if (!query || query.trim().length < 2) { setResults([]); return; }
    try {
      const res = await fetch(`${API_URL}/products?search=${encodeURIComponent(query)}&limit=20`);
      const data = await res.json();
      setResults(data.products || []);
    } catch (err) {
      console.error('Ürün arama hatası:', err);
    }
  };

  useEffect(() => {
    if (activePlatform === 'shopify' && activeTab === 'discounts') {
      fetchDiscountCampaigns();
      fetchCategoriesAndBrands();
      fetchShopifyCollections();
      // Sayfa açıldığında zaten çalışan bir push varsa polling otomatik devam eder,
      // çalışmıyorsa ilk fetch sonrası interval kendini hemen temizler.
      startPollingPushStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlatform, activeTab]);

  useEffect(() => {
    const t = setTimeout(() => searchProductsForPicker(discountProductSearch, setDiscountProductResults), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountProductSearch]);

  useEffect(() => {
    const t = setTimeout(() => searchProductsForPicker(handleProductSearch, setHandleProductResults), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleProductSearch]);

  const handlePreviewDiscount = async () => {
    if (discountForm.scopeType === 'category' && !discountForm.category) return toast.error('Kategori seçin');
    if (discountForm.scopeType === 'brand' && !discountForm.brand) return toast.error('Marka seçin');
    if (discountForm.scopeType === 'products' && discountSelectedProducts.length === 0) return toast.error('En az bir ürün seçin');
    if (discountForm.scopeType === 'shopify_collection' && !discountForm.collectionId) return toast.error('Shopify koleksiyonu seçin');
    if (!discountForm.discountValue || parseFloat(discountForm.discountValue) <= 0) return toast.error('Geçerli bir indirim değeri girin');

    setPreviewingDiscount(true);
    setDiscountPreview(null);
    try {
      const scope = buildScopePayload(discountForm.scopeType, discountForm.category, discountForm.brand, discountSelectedProducts, discountForm.collectionId, discountForm.collectionTitle);
      const res = await fetch(`${API_URL}/shopify/discounts/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope, discount: { type: discountForm.discountType, value: parseFloat(discountForm.discountValue), rounding: discountForm.rounding } })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Önizleme başarısız');
      setDiscountPreview(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPreviewingDiscount(false);
    }
  };

  const handleApplyDiscount = async () => {
    setApplyingDiscount(true);
    try {
      const scope = buildScopePayload(discountForm.scopeType, discountForm.category, discountForm.brand, discountSelectedProducts, discountForm.collectionId, discountForm.collectionTitle);
      const res = await fetch(`${API_URL}/shopify/discounts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: discountForm.name, scope,
          discount: { type: discountForm.discountType, value: parseFloat(discountForm.discountValue), rounding: discountForm.rounding },
          userId: user?.id
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Kampanya oluşturulamadı');
      toast.success(data.updatedExisting
        ? 'Aynı hedefte aktif kampanya vardı, indirim değeri güncellendi ve Shopify\'a uygulanıyor...'
        : 'İndirim kampanyası oluşturuldu, Shopify\'a uygulanıyor...');
      setDiscountForm(emptyDiscountForm);
      setDiscountSelectedProducts([]);
      setDiscountPreview(null);
      fetchDiscountCampaigns();
      startPollingPushStatus();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setApplyingDiscount(false);
    }
  };

  const handlePauseCampaign = async (id) => {
    try {
      await fetch(`${API_URL}/shopify/discounts/${id}/pause`, { method: 'PUT' });
      toast.success('Kampanya duraklatıldı, fiyatlar geri alınıyor...');
      fetchDiscountCampaigns();
      startPollingPushStatus();
    } catch (err) { toast.error('İşlem başarısız'); }
  };

  const handleResumeCampaign = async (id) => {
    try {
      await fetch(`${API_URL}/shopify/discounts/${id}/resume`, { method: 'PUT' });
      toast.success('Kampanya tekrar aktif, fiyatlar uygulanıyor...');
      fetchDiscountCampaigns();
      startPollingPushStatus();
    } catch (err) { toast.error('İşlem başarısız'); }
  };

  const handleEndCampaign = async (id) => {
    try {
      await fetch(`${API_URL}/shopify/discounts/${id}`, { method: 'DELETE' });
      toast.success('Kampanya sonlandırıldı, fiyatlar geri alınıyor...');
      fetchDiscountCampaigns();
      startPollingPushStatus();
    } catch (err) { toast.error('İşlem başarısız'); }
  };

  const handlePreviewHandles = async () => {
    if (handleScopeType === 'category' && !handleCategory) return toast.error('Kategori seçin');
    if (handleScopeType === 'brand' && !handleBrand) return toast.error('Marka seçin');
    if (handleScopeType === 'products' && handleSelectedProducts.length === 0) return toast.error('En az bir ürün seçin');

    setPreviewingHandle(true);
    setHandlePreview(null);
    try {
      const scope = buildScopePayload(handleScopeType, handleCategory, handleBrand, handleSelectedProducts);
      const res = await fetch(`${API_URL}/shopify/handles/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Önizleme başarısız');
      setHandlePreview(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPreviewingHandle(false);
    }
  };

  const handleApplyHandles = async () => {
    setApplyingHandle(true);
    try {
      const scope = buildScopePayload(handleScopeType, handleCategory, handleBrand, handleSelectedProducts);
      const res = await fetch(`${API_URL}/shopify/handles/apply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope, redirect: handleRedirect })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Uygulama başarısız');
      toast.success(`${data.changeCount} ürünün adresi güncelleniyor...`);
      setHandlePreview(null);
      setHandleSelectedProducts([]);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setApplyingHandle(false);
    }
  };

  // ===== TRENDYOL FUNCTIONS =====
  const fetchTrendyolSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/trendyol/settings`);
      const data = await response.json();
      if (data.settings) {
        setTrendyolSettings({
          seller_id: data.settings.seller_id || '',
          api_key: data.settings.api_key || '',
          api_secret: data.settings.api_secret || '',
          integration_code: data.settings.integration_code || '',
          default_deduction_store_id: data.settings.default_deduction_store_id || '',
          sync_enabled: data.settings.sync_enabled === 1,
          commission_percentage: data.settings.commission_percentage || 0,
          price_change_threshold: data.settings.price_change_threshold || 2,
          price_sync_interval: data.settings.price_sync_interval || 0,
          last_synced_gold_price: data.settings.last_synced_gold_price,
          last_synced_usd_rate: data.settings.last_synced_usd_rate,
          last_price_sync_at: data.settings.last_price_sync_at
        });
      }
    } catch (error) {
      console.error('Trendyol ayarları yüklenemedi:', error);
    }
  };

  const saveTrendyolSettings = async () => {
    if (!trendyolSettings.seller_id || !trendyolSettings.api_key || !trendyolSettings.api_secret) {
      toast.error('Satıcı ID, API Key ve API Secret zorunludur');
      return;
    }
    setSavingSettings(true);
    try {
      const response = await fetch(`${API_URL}/trendyol/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trendyolSettings)
      });
      if (response.ok) {
        toast.success('Trendyol ayarları kaydedildi');
      } else {
        const data = await response.json();
        toast.error(data.error || 'Ayarlar kaydedilemedi');
      }
    } catch (error) {
      toast.error('Ayarlar kaydedilemedi');
    } finally {
      setSavingSettings(false);
    }
  };

  const testTrendyolConnection = async () => {
    setTestingConnection(true);
    setTrendyolConnectionStatus(null);
    try {
      const response = await fetch(`${API_URL}/trendyol/test-connection`, { method: 'POST' });
      const data = await response.json();
      setTrendyolConnectionStatus(data.success ? { success: true, message: data.message } : { success: false, message: data.error });
      toast[data.success ? 'success' : 'error'](data.success ? 'Trendyol bağlantısı başarılı!' : (data.error || 'Bağlantı başarısız'));
    } catch (error) {
      setTrendyolConnectionStatus({ success: false, message: error.message });
      toast.error('Bağlantı test edilemedi');
    } finally {
      setTestingConnection(false);
    }
  };

  const disconnectTrendyol = async () => {
    try {
      await fetch(`${API_URL}/trendyol/auth/disconnect`, { method: 'POST' });
      setTrendyolSettings(prev => ({ ...prev, api_key: '', api_secret: '', sync_enabled: false }));
      toast.success('Trendyol bağlantısı kesildi');
    } catch (error) {
      toast.error('Bağlantı kesilemedi');
    }
  };

  const fetchTrendyolProducts = useCallback(async () => {
    setLoadingTrendyol(true);
    try {
      const response = await fetch(`${API_URL}/trendyol/products?page=1&limit=500`);
      const data = await response.json();
      setTrendyolProducts(data.products || []);
    } catch (error) {
      console.error('Trendyol ürünler yüklenemedi:', error);
    } finally {
      setLoadingTrendyol(false);
    }
  }, []);

  const syncAllTrendyolProducts = async () => {
    setSyncingTrendyolProducts(true);
    try {
      const response = await fetch(`${API_URL}/trendyol/products/sync`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        if (data.backgroundSync) pollTrendyolSyncStatus('products');
      } else {
        toast.error(data.error || 'Sync başarısız');
        setSyncingTrendyolProducts(false);
      }
    } catch (error) {
      toast.error('Sync işlemi başarısız');
      setSyncingTrendyolProducts(false);
    }
  };

  const syncSingleTrendyolProduct = async (productId) => {
    try {
      const response = await fetch(`${API_URL}/trendyol/products/${productId}/sync`, { method: 'POST' });
      const data = await response.json();
      toast[data.success ? 'success' : 'error'](data.success ? 'Ürün sync edildi' : (data.error || 'Sync başarısız'));
      if (data.success) fetchTrendyolProducts();
    } catch (error) {
      toast.error('Sync işlemi başarısız');
    }
  };

  const deleteTrendyolProduct = async (productId) => {
    try {
      const response = await fetch(`${API_URL}/trendyol/products/${productId}/sync`, { method: 'DELETE' });
      const data = await response.json();
      toast[data.success ? 'success' : 'error'](data.success ? 'Ürün kaldırıldı' : (data.error || 'Silme başarısız'));
      if (data.success) fetchTrendyolProducts();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const bulkDeleteTrendyolProducts = async () => {
    if (selectedTrendyolProducts.length === 0) return;
    try {
      const response = await fetch(`${API_URL}/trendyol/products/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds: selectedTrendyolProducts })
      });
      const data = await response.json();
      if (data.success) { toast.success(data.message); setSelectedTrendyolProducts([]); fetchTrendyolProducts(); }
      else toast.error(data.error || 'Toplu silme başarısız');
    } catch (error) { toast.error('Toplu silme başarısız'); }
  };

  const syncTrendyolInventory = async () => {
    setSyncingTrendyolInventory(true);
    try {
      const response = await fetch(`${API_URL}/trendyol/inventory/sync`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Stok sync başlatıldı');
        if (data.backgroundSync) pollTrendyolSyncStatus('inventory');
      } else {
        toast.error(data.error || 'Stok sync başarısız');
        setSyncingTrendyolInventory(false);
      }
    } catch (error) {
      toast.error('Stok sync başarısız');
      setSyncingTrendyolInventory(false);
    }
  };

  const syncTrendyolPrices = async () => {
    setSyncingTrendyolPrices(true);
    try {
      const response = await fetch(`${API_URL}/trendyol/prices/sync`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Fiyat sync başlatıldı');
        if (data.backgroundSync) pollTrendyolSyncStatus('prices');
      } else {
        toast.error(data.error || 'Fiyat sync başarısız');
        setSyncingTrendyolPrices(false);
      }
    } catch (error) {
      toast.error('Fiyat sync başarısız');
      setSyncingTrendyolPrices(false);
    }
  };

  const pollTrendyolSyncStatus = (type) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/trendyol/${type}/sync-status`);
        const data = await response.json();
        if (data.syncStatus) {
          setTrendyolSyncProgress(data.syncStatus);
          if (!data.syncStatus.running) {
            clearInterval(interval);
            setTrendyolSyncProgress(null);
            if (type === 'products') { setSyncingTrendyolProducts(false); fetchTrendyolProducts(); }
            else if (type === 'inventory') setSyncingTrendyolInventory(false);
            else if (type === 'prices') setSyncingTrendyolPrices(false);
            toast.success(`Trendyol ${type === 'products' ? 'ürün' : type === 'prices' ? 'fiyat' : 'stok'} sync: ${data.syncStatus.success} başarılı, ${data.syncStatus.failed} hata`);
          }
        }
      } catch (error) { clearInterval(interval); }
    }, 3000);
  };

  const fetchTrendyolOrders = useCallback(async () => {
    try {
      const statusParam = trendyolOrderFilter !== 'all' ? `&status=${trendyolOrderFilter}` : '';
      const response = await fetch(`${API_URL}/trendyol/orders?page=1&limit=100${statusParam}`);
      const data = await response.json();
      setTrendyolOrders(data.orders || []);
      setTrendyolNewOrderCount((data.orders || []).filter(o => o.sync_status === 'pending').length);
    } catch (error) {
      console.error('Trendyol siparişler yüklenemedi:', error);
    }
  }, [trendyolOrderFilter]);

  const pullTrendyolOrders = async () => {
    setPullingTrendyolOrders(true);
    try {
      const response = await fetch(`${API_URL}/trendyol/orders/pull`, { method: 'POST' });
      const data = await response.json();
      toast[data.success ? 'success' : 'error'](data.success ? data.message : (data.error || 'Sipariş çekme başarısız'));
      if (data.success) fetchTrendyolOrders();
    } catch (error) {
      toast.error('Sipariş çekme başarısız');
    } finally {
      setPullingTrendyolOrders(false);
    }
  };

  const processTrendyolOrder = async (orderId) => {
    try {
      const response = await fetch(`${API_URL}/trendyol/orders/${orderId}/process`, { method: 'POST' });
      const data = await response.json();
      toast[data.success ? 'success' : 'error'](data.success ? 'Sipariş işlendi' : (data.error || 'İşleme başarısız'));
      if (data.success) fetchTrendyolOrders();
    } catch (error) { toast.error('İşleme başarısız'); }
  };

  const bulkProcessTrendyolOrders = async () => {
    try {
      const body = selectedTrendyolOrders.length > 0 ? { orderIds: selectedTrendyolOrders } : { processAll: true };
      const response = await fetch(`${API_URL}/trendyol/orders/bulk-process`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      const data = await response.json();
      if (data.success) { toast.success(data.message); setSelectedTrendyolOrders([]); fetchTrendyolOrders(); }
      else toast.error(data.error || 'Toplu işleme başarısız');
    } catch (error) { toast.error('Toplu işleme başarısız'); }
  };

  const bulkDeleteTrendyolOrders = async () => {
    try {
      const body = selectedTrendyolOrders.length > 0 ? { orderIds: selectedTrendyolOrders } : { deleteAll: true };
      const response = await fetch(`${API_URL}/trendyol/orders/bulk-delete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      const data = await response.json();
      if (data.success) { toast.success(data.message); setSelectedTrendyolOrders([]); fetchTrendyolOrders(); }
      else toast.error(data.error || 'Toplu silme başarısız');
    } catch (error) { toast.error('Toplu silme başarısız'); }
  };

  const fetchTrendyolCustomers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/trendyol/customers-from-orders`);
      const data = await response.json();
      setTrendyolCustomers(data.customers || []);
    } catch (error) { console.error('Trendyol müşteriler yüklenemedi:', error); }
  }, []);

  const fetchTrendyolLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: 1, limit: 100 });
      if (logFilter.type) params.set('type', logFilter.type);
      if (logFilter.status) params.set('status', logFilter.status);
      const response = await fetch(`${API_URL}/trendyol/sync-logs?${params}`);
      const data = await response.json();
      setTrendyolLogs(data.logs || []);
    } catch (error) { console.error('Trendyol loglar yüklenemedi:', error); }
  }, [logFilter]);

  // Trendyol data fetch on tab/platform change
  useEffect(() => {
    if (activePlatform !== 'trendyol') return;
    fetchTrendyolSettings();
    if (activeTab === 'products') fetchTrendyolProducts();
    else if (activeTab === 'orders') fetchTrendyolOrders();
    else if (activeTab === 'customers') fetchTrendyolCustomers();
    else if (activeTab === 'logs') fetchTrendyolLogs();
  }, [activePlatform, activeTab, fetchTrendyolProducts, fetchTrendyolOrders, fetchTrendyolCustomers, fetchTrendyolLogs]);

  // Fetch stores
  const fetchStores = async () => {
    try {
      const response = await fetch(`${API_URL}/stores`);
      const data = await response.json();
      setStores(data);
    } catch (error) {
      console.error('Mağazalar yüklenemedi:', error);
    }
  };

  // Fetch settings
  const fetchSettings = async () => {
    setLoadingSettings(true);
    try {
      const response = await fetch(`${API_URL}/shopify/settings`);
      const data = await response.json();
      if (data.settings) {
        setSettings({
          shop_url: data.settings.shop_url || '',
          access_token: data.settings.access_token || '',
          api_version: data.settings.api_version || '2024-01',
          webhook_secret: data.settings.webhook_secret || '',
          default_deduction_store_id: data.settings.default_deduction_store_id || '',
          sync_enabled: data.settings.sync_enabled === 1,
          store_currency: data.settings.store_currency || 'USD',
          exchange_rate_try_to_usd: data.settings.exchange_rate_try_to_usd || 0.0286,
          commission_percentage: data.settings.commission_percentage || 0,
          auto_publish: data.settings.auto_publish === 1,
          price_change_threshold: data.settings.price_change_threshold || 2,
          price_sync_interval: data.settings.price_sync_interval || 0,
          last_synced_gold_price: data.settings.last_synced_gold_price,
          last_synced_usd_rate: data.settings.last_synced_usd_rate,
          last_price_sync_at: data.settings.last_price_sync_at
        });
      }
    } catch (error) {
      console.error('Shopify ayarları yüklenemedi:', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  // Save settings
  const saveSettings = async () => {
    if (!settings.shop_url || !settings.access_token) {
      toast.error('Shop URL ve Access Token zorunludur');
      return;
    }

    setSavingSettings(true);
    try {
      const response = await fetch(`${API_URL}/shopify/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Shopify ayarları kaydedildi');
        logService.log('shopify', 'update', 'Shopify ayarları güncellendi', {
          status: 'success',
          details: { shop_url: settings.shop_url, sync_enabled: settings.sync_enabled }
        });
      } else {
        const data = await response.json();
        toast.error(data.error || 'Ayarlar kaydedilemedi');
        logService.log('shopify', 'update', 'Shopify ayarları kaydedilemedi', {
          status: 'error',
          details: { error: data.error }
        });
      }
    } catch (error) {
      console.error('Ayar kaydetme hatası:', error);
      toast.error('Ayarlar kaydedilemedi');
      logService.log('shopify', 'update', 'Shopify ayar kaydetme hatası', {
        status: 'error',
        details: { error: error.message }
      });
    } finally {
      setSavingSettings(false);
    }
  };

  // ===== MULTI-LOCATION FUNCTIONS =====

  // Shopify locations'ı çek
  const fetchShopifyLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await fetch(`${API_URL}/shopify/locations`);
      const data = await response.json();
      if (data.locations) {
        setShopifyLocations(data.locations);
      }
    } catch (error) {
      console.error('Shopify locations yüklenemedi:', error);
      toast.error('Shopify lokasyonları alınamadı');
    } finally {
      setLoadingLocations(false);
    }
  };

  // Şube-location eşlemelerini çek
  const fetchStoreLocationMappings = async () => {
    try {
      const response = await fetch(`${API_URL}/shopify/store-locations`);
      const data = await response.json();
      setStoreLocationMappings(data);
    } catch (error) {
      console.error('Store mappings yüklenemedi:', error);
    }
  };

  // Şube-location eşlemesini güncelle
  const updateStoreLocationMapping = (storeId, shopifyLocationId) => {
    setStoreLocationMappings(prev =>
      prev.map(store =>
        store.id === storeId
          ? { ...store, shopify_location_id: shopifyLocationId || null }
          : store
      )
    );
  };

  // Tüm eşlemeleri kaydet
  const saveStoreLocationMappings = async () => {
    setSavingMappings(true);
    try {
      const mappings = storeLocationMappings.map(store => ({
        store_id: store.id,
        shopify_location_id: store.shopify_location_id
      }));

      const response = await fetch(`${API_URL}/shopify/store-locations`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mappings })
      });

      if (response.ok) {
        toast.success('Şube-lokasyon eşlemeleri kaydedildi');
      } else {
        const data = await response.json();
        toast.error(data.error || 'Eşlemeler kaydedilemedi');
      }
    } catch (error) {
      console.error('Mapping kaydetme hatası:', error);
      toast.error('Eşlemeler kaydedilemedi');
    } finally {
      setSavingMappings(false);
    }
  };

  // Multi-location stok senkronizasyonu
  const syncInventoryMultiLocation = async () => {
    setSyncingMultiLocation(true);
    try {
      const response = await fetch(`${API_URL}/shopify/inventory/sync-multi-location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(`Multi-location sync: ${data.message}`);
        if (data.errors && data.errors.length > 0) {
          console.warn('Sync hataları:', data.errors);
        }
      } else {
        toast.error(data.error || 'Senkronizasyon başarısız');
      }
    } catch (error) {
      console.error('Multi-location sync hatası:', error);
      toast.error('Senkronizasyon başarısız');
    } finally {
      setSyncingMultiLocation(false);
    }
  };

  // Settings tab açıldığında locations'ı yükle
  useEffect(() => {
    if (activeTab === 'settings' && settings.shop_url) {
      fetchShopifyLocations();
      fetchStoreLocationMappings();
    }
  }, [activeTab, settings.shop_url]);

  // Test connection
  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const response = await fetch(`${API_URL}/shopify/test-connection`, {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        setConnectionStatus({
          success: true,
          shop: data.shop,
          message: 'Bağlantı başarılı!'
        });
        toast.success('Shopify bağlantısı başarılı!');
      } else {
        setConnectionStatus({
          success: false,
          message: data.error || 'Bağlantı başarısız'
        });
        toast.error(data.error || 'Bağlantı başarısız');
      }
    } catch (error) {
      setConnectionStatus({
        success: false,
        message: 'Bağlantı test edilemedi'
      });
      toast.error('Bağlantı test edilemedi');
    } finally {
      setTestingConnection(false);
    }
  };

  // Check OAuth status
  const checkOAuthStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/shopify/auth/status`);
      const data = await response.json();
      setOauthStatus({
        connected: data.connected,
        shop: data.shop,
        loading: false
      });
    } catch (error) {
      console.error('OAuth status check error:', error);
      setOauthStatus({ connected: false, loading: false });
    }
  };

  // Initiate OAuth flow
  const initiateOAuth = async () => {
    setConnectingOAuth(true);
    try {
      const response = await fetch(`${API_URL}/shopify/auth`);
      const data = await response.json();

      if (data.authUrl) {
        // Redirect to Shopify OAuth
        window.location.href = data.authUrl;
      } else {
        toast.error(data.error || 'OAuth URL alınamadı');
      }
    } catch (error) {
      console.error('OAuth initiation error:', error);
      toast.error('Shopify bağlantısı başlatılamadı');
    } finally {
      setConnectingOAuth(false);
    }
  };

  // Disconnect OAuth
  const disconnectOAuth = async () => {
    showConfirmation({
      title: 'Bağlantıyı Kes',
      message: 'Shopify bağlantısını kesmek istediğinize emin misiniz?',
      confirmText: 'Bağlantıyı Kes',
      cancelText: 'İptal',
      type: 'warning',
      onConfirm: async () => {
        setDisconnectingOAuth(true);
        try {
          const response = await fetch(`${API_URL}/shopify/auth/disconnect`, {
            method: 'POST'
          });
          const data = await response.json();

          if (data.success) {
            toast.success('Shopify bağlantısı kesildi');
            logService.log('shopify', 'update', 'Shopify OAuth bağlantısı kesildi', {
              status: 'warning',
              details: { action: 'disconnect' }
            });
            setOauthStatus({ connected: false, loading: false });
            setConnectionStatus(null);
            setSettings(prev => ({ ...prev, access_token: '' }));
          } else {
            toast.error(data.error || 'Bağlantı kesilemedi');
          }
        } catch (error) {
          console.error('OAuth disconnect error:', error);
          toast.error('Bağlantı kesilemedi');
          logService.log('shopify', 'update', 'Shopify OAuth bağlantı kesme hatası', {
            status: 'error',
            details: { error: error.message }
          });
        } finally {
          setDisconnectingOAuth(false);
        }
      }
    });
  };

  // Fetch products
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await fetch(`${API_URL}/shopify/products?limit=2000&include_shopify_only=false`);
      const data = await response.json();
      setProducts(data.products || []);
    } catch (error) {
      console.error('Ürünler yüklenemedi:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Sync all products
  const syncAllProducts = async () => {
    setSyncingProducts(true);
    try {
      const response = await fetch(`${API_URL}/shopify/products/sync`, {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        if (data.backgroundSync) {
          toast.success(data.message);
          setSyncProgress({ type: 'product', running: true, success: 0, failed: 0, total: data.syncStatus?.total || 0 });
          // Arka plan sync'ini takip et
          const pollInterval = setInterval(async () => {
            try {
              const statusRes = await fetch(`${API_URL}/shopify/products/sync-status`);
              const statusData = await statusRes.json();
              const s = statusData.syncStatus;
              setSyncProgress({ type: 'product', running: s.running, success: s.success, failed: s.failed, total: s.total });
              if (!s.running) {
                clearInterval(pollInterval);
                setSyncingProducts(false);
                setSyncProgress(null);
                toast.success(`Sync tamamlandı: ${s.success} başarılı, ${s.failed} hata`);
                logService.log('shopify', 'sync', `Tüm ürünler sync edildi: ${s.success} başarılı, ${s.failed} hata`, {
                  status: s.failed > 0 ? 'warning' : 'success',
                  details: { success: s.success, failed: s.failed, errors: s.errors }
                });
                fetchProducts();
                
              }
            } catch (e) {
              // Polling hatası - devam et
            }
          }, 2000);
        } else {
          toast.success(data.message);
          fetchProducts();
          
          setSyncingProducts(false);
        }
      } else {
        toast.error(data.error || 'Sync başarısız');
        logService.log('shopify', 'sync', 'Ürün sync başarısız', {
          status: 'error',
          details: { error: data.error }
        });
        setSyncingProducts(false);
      }
    } catch (error) {
      toast.error('Sync işlemi başarısız');
      logService.log('shopify', 'sync', 'Ürün sync hatası', {
        status: 'error',
        details: { error: error.message }
      });
      setSyncingProducts(false);
    }
  };

  // Sync single product
  const syncProduct = async (productId) => {
    const product = products.find(p => p.id === productId);
    try {
      const response = await fetch(`${API_URL}/shopify/products/${productId}/sync`, {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Ürün sync edildi');
        logService.log('shopify', 'sync', `Ürün Shopify'a sync edildi: ${product?.name || productId}`, {
          entity_type: 'product',
          entity_id: productId,
          status: 'success'
        });
        fetchProducts();
      } else {
        toast.error(data.error || 'Sync başarısız');
        logService.log('shopify', 'sync', `Ürün sync başarısız: ${product?.name || productId}`, {
          status: 'error',
          details: { error: data.error }
        });
      }
    } catch (error) {
      toast.error('Sync işlemi başarısız');
    }
  };

  // Delete product from Shopify
  const deleteFromShopify = async (productId) => {
    const product = products.find(p => p.id === productId);
    showConfirmation({
      title: 'Ürünü Sil',
      message: 'Bu ürün Shopify\'dan silinecek. Emin misiniz?',
      confirmText: 'Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/shopify/products/${productId}/sync`, {
            method: 'DELETE'
          });
          const data = await response.json();

          if (data.success) {
            toast.success('Ürün Shopify\'dan silindi');
            logService.log('shopify', 'delete', `Ürün Shopify'dan silindi: ${product?.name || productId}`, {
              entity_type: 'product',
              entity_id: productId,
              status: 'warning',
              details: { product_name: product?.name }
            });
            setSelectedProducts([]);
            fetchProducts();
          } else {
            toast.error(data.error || 'Silme başarısız');
            logService.log('shopify', 'delete', `Ürün Shopify'dan silinemedi: ${product?.name || productId}`, {
              status: 'error',
              details: { error: data.error }
            });
          }
        } catch (error) {
          toast.error('Silme işlemi başarısız');
          logService.log('shopify', 'delete', `Ürün silme hatası: ${productId}`, {
            status: 'error',
            details: { error: error.message }
          });
        }
      }
    });
  };

  // Bulk delete selected products
  const bulkDeleteProducts = async (deleteAll = false) => {
    if (!deleteAll && selectedProducts.length === 0) {
      toast.warning('Lütfen silinecek ürünleri seçin');
      return;
    }

    const syncedCount = products.filter(p => p.shopify_product_id).length;
    const message = deleteAll
      ? `TÜM ${syncedCount} sync edilmiş ürün Shopify'dan silinecek. Bu işlem geri alınamaz! Emin misiniz?`
      : `${selectedProducts.length} seçili ürün Shopify'dan silinecek. Emin misiniz?`;

    showConfirmation({
      title: 'Toplu Silme',
      message: message,
      confirmText: 'Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        setBulkDeleting(true);
        const deleteCount = deleteAll ? products.filter(p => p.shopify_product_id).length : selectedProducts.length;
        try {
          const response = await fetch(`${API_URL}/shopify/products/bulk-delete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              productIds: deleteAll ? undefined : selectedProducts,
              deleteAll: deleteAll
            })
          });
          const data = await response.json();

          if (data.success) {
            toast.success(data.message);
            logService.log('shopify', 'bulk_delete', `Toplu ürün silme: ${deleteCount} ürün Shopify'dan silindi`, {
              status: 'warning',
              details: {
                delete_count: deleteCount,
                delete_all: deleteAll,
                message: data.message
              }
            });
            setSelectedProducts([]);
            fetchProducts();
            
          } else {
            toast.error(data.error || 'Toplu silme başarısız');
            logService.log('shopify', 'bulk_delete', 'Toplu ürün silme başarısız', {
              status: 'error',
              details: { error: data.error }
            });
          }
        } catch (error) {
          console.error('Toplu silme hatası:', error);
          toast.error('Toplu silme işlemi başarısız');
          logService.log('shopify', 'bulk_delete', 'Toplu ürün silme hatası', {
            status: 'error',
            details: { error: error.message }
          });
        } finally {
          setBulkDeleting(false);
        }
      }
    });
  };

  // Toggle product selection
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // Select all products
  const toggleSelectAll = () => {
    const syncedProducts = filteredProducts.filter(p => p.shopify_product_id);
    if (selectedProducts.length === syncedProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(syncedProducts.map(p => p.id));
    }
  };

  // Sync inventory (Multi-location) - async with polling
  const syncInventory = async () => {
    setSyncingInventory(true);
    try {
      const response = await fetch(`${API_URL}/shopify/inventory/sync-multi-location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}) // Tüm ürünler için
      });
      const data = await response.json();

      if (data.success) {
        if (data.backgroundSync) {
          toast.success(data.message);
          setSyncProgress({ type: 'inventory', running: true, success: 0, failed: 0, total: data.syncStatus?.total || 0 });
          // Polling ile takip et
          const pollInterval = setInterval(async () => {
            try {
              const statusRes = await fetch(`${API_URL}/shopify/inventory/sync-status`);
              const statusData = await statusRes.json();
              const s = statusData.syncStatus;
              setSyncProgress({ type: 'inventory', running: s.running, success: s.success, failed: s.failed, total: s.total });
              if (!s.running) {
                clearInterval(pollInterval);
                setSyncingInventory(false);
                setSyncProgress(null);
                toast.success(`Stok sync tamamlandı: ${s.success} başarılı, ${s.failed} hata`);
                logService.log('shopify', 'sync', `Stok Shopify'a sync edildi: ${s.success} başarılı, ${s.failed} hata`, {
                  status: s.failed > 0 ? 'warning' : 'success',
                  details: { success: s.success, failed: s.failed, errors: s.errors }
                });
                fetchProducts();
              }
            } catch (e) {
              // Polling hatası - devam et
            }
          }, 2000);
        } else {
          toast.success(data.message);
          logService.log('shopify', 'sync', `Stok Shopify'a sync edildi`, {
            status: 'success',
            details: { message: data.message }
          });
          fetchProducts();
          setSyncingInventory(false);
        }
      } else {
        toast.error(data.error || 'Stok sync başarısız');
        logService.log('shopify', 'sync', 'Stok sync başarısız', {
          status: 'error',
          details: { error: data.error }
        });
        setSyncingInventory(false);
      }
    } catch (error) {
      toast.error('Stok sync işlemi başarısız');
      logService.log('shopify', 'sync', 'Stok sync hatası', {
        status: 'error',
        details: { error: error.message }
      });
      setSyncingInventory(false);
    }
  };

  // Sync prices to Shopify - async with polling
  const syncPrices = async () => {
    setSyncingPrices(true);
    try {
      const response = await fetch(`${API_URL}/shopify/prices/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();

      if (data.success) {
        if (data.backgroundSync) {
          toast.success(data.message);
          setSyncProgress({ type: 'price', running: true, success: 0, failed: 0, total: data.total || 0 });
          // Polling ile takip et
          const pollInterval = setInterval(async () => {
            try {
              const statusRes = await fetch(`${API_URL}/shopify/prices/sync-status`);
              const statusData = await statusRes.json();
              const s = statusData.syncStatus;
              setSyncProgress({ type: 'price', running: s.running, success: s.success, failed: s.failed, total: s.total });
              if (!s.running) {
                clearInterval(pollInterval);
                setSyncingPrices(false);
                setSyncProgress(null);
                toast.success(`Fiyat sync tamamlandı: ${s.success} başarılı, ${s.failed} hata`);
                logService.log('shopify', 'sync', `Fiyatlar Shopify'a sync edildi: ${s.success} başarılı, ${s.failed} hata`, {
                  status: s.failed > 0 ? 'warning' : 'success',
                  details: { success: s.success, failed: s.failed, errors: s.errors }
                });
                fetchProducts();
              }
            } catch (e) {
              // Polling hatası - devam et
            }
          }, 2000);
        } else {
          toast.success(data.message);
          logService.log('shopify', 'sync', `Fiyatlar Shopify'a sync edildi: ${data.synced} başarılı`, {
            status: data.failed > 0 ? 'warning' : 'success',
            details: { synced: data.synced, failed: data.failed, errors: data.errors }
          });
          fetchProducts();
          setSyncingPrices(false);
        }
      } else {
        toast.error(data.error || 'Fiyat sync başarısız');
        logService.log('shopify', 'sync', 'Fiyat sync başarısız', {
          status: 'error',
          details: { error: data.error }
        });
        setSyncingPrices(false);
      }
    } catch (error) {
      toast.error('Fiyat sync işlemi başarısız');
      logService.log('shopify', 'sync', 'Fiyat sync hatası', {
        status: 'error',
        details: { error: error.message }
      });
      setSyncingPrices(false);
    }
  };

  // Fetch orders
  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      let url = `${API_URL}/shopify/orders?limit=100`;
      if (orderFilter !== 'all') {
        url += `&status=${orderFilter}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      setOrders(data.orders || []);
    } catch (error) {
      console.error('Siparişler yüklenemedi:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  // Pull orders from Shopify
  const pullOrders = async () => {
    setPullingOrders(true);
    try {
      const response = await fetch(`${API_URL}/shopify/orders/pull`, {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        toast.success(data.message);
        logService.log('shopify', 'sync', `Siparişler Shopify'dan çekildi`, {
          status: 'success',
          details: { message: data.message }
        });
        fetchOrders();
        
      } else {
        toast.error(data.error || 'Sipariş çekme başarısız');
        logService.log('shopify', 'sync', 'Sipariş çekme başarısız', {
          status: 'error',
          details: { error: data.error }
        });
      }
    } catch (error) {
      toast.error('Sipariş çekme işlemi başarısız');
      logService.log('shopify', 'sync', 'Sipariş çekme hatası', {
        status: 'error',
        details: { error: error.message }
      });
    } finally {
      setPullingOrders(false);
    }
  };

  // Process order manually
  const processOrder = async (orderId) => {
    const order = orders.find(o => o.id === orderId);
    try {
      const response = await fetch(`${API_URL}/shopify/orders/${orderId}/process`, {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Sipariş işlendi');
        logService.log('shopify', 'create', `Shopify siparişi işlendi: #${order?.shopify_order_id || orderId}`, {
          entity_type: 'shopify_order',
          entity_id: orderId,
          status: 'success',
          details: {
            shopify_order_id: order?.shopify_order_id,
            total: order?.total_price
          }
        });
        setSelectedOrders([]);
        fetchOrders();
        
      } else {
        toast.error(data.error || 'İşleme başarısız');
        logService.log('shopify', 'create', `Sipariş işleme başarısız: #${orderId}`, {
          status: 'error',
          details: { error: data.error }
        });
      }
    } catch (error) {
      toast.error('Sipariş işleme başarısız');
      logService.log('shopify', 'create', `Sipariş işleme hatası: #${orderId}`, {
        status: 'error',
        details: { error: error.message }
      });
    }
  };

  // Bulk process orders
  const bulkProcessOrders = async (processAll = false) => {
    if (!processAll && selectedOrders.length === 0) {
      toast.warning('Lütfen işlenecek siparişleri seçin');
      return;
    }

    // Filter to only pending/error orders from selection
    const processableSelected = processAll ? [] : selectedOrders.filter(id => {
      const order = orders.find(o => o.id === id);
      return order && (order.sync_status === 'pending' || order.sync_status === 'error');
    });

    if (!processAll && processableSelected.length === 0) {
      toast.warning('Seçili siparişler arasında işlenebilir (bekleyen/hatalı) sipariş yok');
      return;
    }

    const pendingCount = orders.filter(o => o.sync_status === 'pending' || o.sync_status === 'error').length;
    const message = processAll
      ? `TÜM ${pendingCount} bekleyen/hatalı sipariş ERP'ye işlenecek. Emin misiniz?`
      : `${processableSelected.length} seçili bekleyen/hatalı sipariş ERP'ye işlenecek. Emin misiniz?`;

    showConfirmation({
      title: 'Siparişleri İşle',
      message: message,
      confirmText: 'İşle',
      cancelText: 'İptal',
      type: 'warning',
      onConfirm: async () => {
        setBulkProcessing(true);
        const processCount = processAll
          ? orders.filter(o => o.sync_status === 'pending' || o.sync_status === 'error').length
          : selectedOrders.length;
        try {
          const response = await fetch(`${API_URL}/shopify/orders/bulk-process`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              orderIds: processAll ? undefined : selectedOrders,
              processAll: processAll
            })
          });
          const data = await response.json();

          if (data.success) {
            toast.success(data.message);
            logService.log('shopify', 'bulk_process', `Toplu sipariş işleme: ${processCount} sipariş işlendi`, {
              status: 'success',
              details: {
                process_count: processCount,
                process_all: processAll,
                message: data.message
              }
            });
            setSelectedOrders([]);
            fetchOrders();
            
          } else {
            toast.error(data.error || 'Toplu işleme başarısız');
            logService.log('shopify', 'bulk_process', 'Toplu sipariş işleme başarısız', {
              status: 'error',
              details: { error: data.error }
            });
          }
        } catch (error) {
          console.error('Toplu işleme hatası:', error);
          toast.error('Toplu işleme başarısız');
          logService.log('shopify', 'bulk_process', 'Toplu sipariş işleme hatası', {
            status: 'error',
            details: { error: error.message }
          });
        } finally {
          setBulkProcessing(false);
        }
      }
    });
  };

  // Toggle order selection
  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  // Select all orders (any status for deletion, only pending/error for processing)
  const toggleSelectAllOrders = () => {
    // For selection, allow all orders (can be deleted), but only pending/error can be processed
    if (selectedOrders.length === filteredOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map(o => o.id));
    }
  };

  // Bulk delete orders
  const bulkDeleteOrders = async (deleteAll = false) => {
    if (!deleteAll && selectedOrders.length === 0) {
      toast.warning('Lütfen silinecek siparişleri seçin');
      return;
    }

    const message = deleteAll
      ? `TÜM ${orders.length} sipariş silinecek. Bu işlem geri alınamaz! Emin misiniz?`
      : `${selectedOrders.length} seçili sipariş silinecek. Emin misiniz?`;

    showConfirmation({
      title: 'Siparişleri Sil',
      message: message,
      confirmText: 'Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        setBulkDeletingOrders(true);
        const deleteCount = deleteAll ? orders.length : selectedOrders.length;
        try {
          const response = await fetch(`${API_URL}/shopify/orders/bulk-delete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              orderIds: deleteAll ? undefined : selectedOrders,
              deleteAll: deleteAll
            })
          });
          const data = await response.json();

          if (data.success) {
            toast.success(data.message);
            logService.log('shopify', 'bulk_delete', `Toplu sipariş silme: ${deleteCount} sipariş silindi`, {
              status: 'warning',
              details: {
                delete_count: deleteCount,
                delete_all: deleteAll,
                message: data.message
              }
            });
            setSelectedOrders([]);
            fetchOrders();
            
          } else {
            toast.error(data.error || 'Toplu silme başarısız');
            logService.log('shopify', 'bulk_delete', 'Toplu sipariş silme başarısız', {
              status: 'error',
              details: { error: data.error }
            });
          }
        } catch (error) {
          console.error('Toplu silme hatası:', error);
          toast.error('Toplu silme işlemi başarısız');
          logService.log('shopify', 'bulk_delete', 'Toplu sipariş silme hatası', {
            status: 'error',
            details: { error: error.message }
          });
        } finally {
          setBulkDeletingOrders(false);
        }
      }
    });
  };

  // Fetch customers - önce siparişlerden, sonra Shopify API'dan
  const fetchCustomers = async () => {
    setLoadingCustomers(true);
    try {
      // Önce siparişlerden müşteri verisi çek (daha güvenilir)
      const ordersResponse = await fetch(`${API_URL}/shopify/customers-from-orders`);
      const ordersData = await ordersResponse.json();

      if (ordersData.success && ordersData.customers && ordersData.customers.length > 0) {
        setCustomers(ordersData.customers);
        return;
      }

      // Sipariş yoksa Shopify API'dan çek
      const response = await fetch(`${API_URL}/shopify/customers?limit=100`);
      const data = await response.json();
      if (data.success) {
        setCustomers(data.customers || []);
      } else {
        console.error('Müşteriler yüklenemedi:', data.error);
      }
    } catch (error) {
      console.error('Müşteriler yüklenemedi:', error);
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Fetch customer detail with orders
  const fetchCustomerDetail = async (customerId) => {
    setLoadingCustomerDetail(true);
    try {
      const response = await fetch(`${API_URL}/shopify/customers/${customerId}`);
      const data = await response.json();
      if (data.success) {
        setSelectedCustomer(data.customer);
        setCustomerOrders(data.orders || []);
      } else {
        toast.error('Müşteri detayları yüklenemedi');
      }
    } catch (error) {
      console.error('Müşteri detay hatası:', error);
      toast.error('Müşteri detayları yüklenemedi');
    } finally {
      setLoadingCustomerDetail(false);
    }
  };

  // Fetch logs
  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      let url = `${API_URL}/shopify/sync-logs?limit=100`;
      if (logFilter.type) url += `&type=${logFilter.type}`;
      if (logFilter.status) url += `&status=${logFilter.status}`;

      const response = await fetch(url);
      const data = await response.json();
      setLogs(data.logs || []);
    } catch (error) {
      console.error('Loglar yüklenemedi:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Auto-pull orders from Shopify (silent, no toast)
  const autoPullOrders = useCallback(async () => {
    if (!settings.shop_url || !settings.access_token) return;

    try {
      const response = await fetch(`${API_URL}/shopify/orders/pull`, {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success && data.newOrders > 0) {
        setNewOrderCount(prev => prev + data.newOrders);
        toast.info(`🛍️ ${data.newOrders} yeni sipariş alındı!`, { autoClose: 7500 });
      }

      setLastOrderSync(new Date());
      fetchOrders();
    } catch (error) {
      console.error('Auto order pull error:', error);
    }
  }, [settings.shop_url, settings.access_token]);

  // Auto-sync products inventory (silent) - Multi-location kullanır
  const autoSyncInventory = useCallback(async () => {
    if (!settings.shop_url || !settings.access_token) return;

    try {
      // Multi-location sync kullan (eski tek lokasyon yerine)
      await fetch(`${API_URL}/shopify/inventory/sync-multi-location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}) // Tüm ürünler için
      });
      setLastProductSync(new Date());
      if (activeTab === 'products') {
        fetchProducts();
      }
    } catch (error) {
      console.error('Auto inventory sync error:', error);
    }
  }, [settings.shop_url, settings.access_token, activeTab]);

  // Start/stop auto-polling
  useEffect(() => {
    if (autoSyncEnabled && settings.sync_enabled && settings.shop_url) {
      // Start polling
      setIsLive(true);

      // Initial fetch
      autoPullOrders();
      autoSyncInventory();

      // Set up intervals
      ordersIntervalRef.current = setInterval(autoPullOrders, ORDERS_POLL_INTERVAL);
      productsIntervalRef.current = setInterval(autoSyncInventory, PRODUCTS_POLL_INTERVAL);

    } else {
      setIsLive(false);
    }

    // Cleanup
    return () => {
      if (ordersIntervalRef.current) clearInterval(ordersIntervalRef.current);
      if (productsIntervalRef.current) clearInterval(productsIntervalRef.current);
    };
  }, [autoSyncEnabled, settings.sync_enabled, settings.shop_url, autoPullOrders, autoSyncInventory, activeTab]);

  // Load data when tab changes
  useEffect(() => {
    fetchStores();
    fetchSettings();
    checkOAuthStatus();

    // Check for OAuth callback success/error
    const urlParams = new URLSearchParams(window.location.search);
    const oauthSuccess = urlParams.get('oauth_success');
    const oauthError = urlParams.get('oauth_error');

    if (oauthSuccess === 'true') {
      toast.success('Shopify bağlantısı başarılı!');
      checkOAuthStatus();
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      setActiveTab('settings');
    } else if (oauthError) {
      toast.error(`Shopify bağlantı hatası: ${oauthError}`);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      setActiveTab('settings');
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts();
    } else if (activeTab === 'orders') {
      fetchOrders();
      setNewOrderCount(0); // Reset new order count when viewing orders
    } else if (activeTab === 'customers') {
      fetchCustomers();
    } else if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [activeTab, orderFilter]);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [logFilter]);

  // Filter products
  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(productFilter.toLowerCase()) ||
    p.barcode?.includes(productFilter)
  );

  // Filter orders
  const filteredOrders = orders.filter(order => {
    if (!orderSearchQuery) return true;
    return (
      order.shopify_order_number?.toString().includes(orderSearchQuery) ||
      order.customer_name?.toLowerCase().includes(orderSearchQuery) ||
      order.customer_email?.toLowerCase().includes(orderSearchQuery)
    );
  });

  // Get sync status badge
  const getSyncStatusBadge = (status) => {
    const badges = {
      synced: { class: 'badge-success', text: 'Sync' },
      pending: { class: 'badge-warning', text: 'Bekliyor' },
      error: { class: 'badge-danger', text: 'Hata' },
      deleted: { class: 'badge-secondary', text: 'Silindi' },
      processing: { class: 'badge-info', text: 'İşleniyor' },
      skipped: { class: 'badge-secondary', text: 'Atlandı' }
    };
    const badge = badges[status] || { class: 'badge-secondary', text: status || 'Yeni' };
    return <span className={`sync-badge ${badge.class}`}>{badge.text}</span>;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('tr-TR');
  };

  // Format currency - displays price in product's currency
  const formatCurrency = (amount, currency = 'TRY') => {
    // Convert invalid currency codes
    let productCurrency = currency || 'TRY';
    if (productCurrency === 'TL') productCurrency = 'TRY';

    const locale = productCurrency === 'USD' ? 'en-US' : 'tr-TR';
    return new Intl.NumberFormat(locale, { style: 'currency', currency: productCurrency }).format(amount || 0);
  };

  // Format time ago
  const formatTimeAgo = (date) => {
    if (!date) return 'Henüz yok';
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return `${seconds} saniye önce`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} dakika önce`;
    const hours = Math.floor(minutes / 60);
    return `${hours} saat önce`;
  };

  return (
    <div className="shopify-container">
      {/* Header */}
      <header className="shopify-header">
        <div className="header-left">
          <button className="back-button" onClick={() => navigate('/')}>
            <ArrowLeft size={18} /> Ana Sayfa
          </button>
          <h1>Pazaryeri Entegrasyonu</h1>
          {isLive && (
            <span className="live-indicator">
              <span className="live-dot"></span>
              CANLI
            </span>
          )}
        </div>
        <div className="header-center">
          <img
            src={(user?.store_id === 1 || selectedStore?.id === 1) ? "/Siyah - Beyaz - 1.png" : "/logo.png"}
            alt="Akik"
            className="header-logo"
          />
        </div>
        <div className="header-right">
          <a
            href={PAZARYERI_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              background: 'rgba(255,255,255,0.15)', padding: '6px 12px', borderRadius: '20px',
              color: '#fff', fontSize: '0.85rem', fontWeight: 500, textDecoration: 'none',
            }}
            title="Yeni ilan hazırlama ve yayınlama uygulaması, ayrı sekmede açılır"
          >
            <ExternalLink size={14} /> AkikPazar'a Git
          </a>
          <div className="sync-status">
            {lastOrderSync && (
              <span className="sync-time" title="Son sipariş kontrolü">
                <ShoppingBag size={14} /> {formatTimeAgo(lastOrderSync)}
              </span>
            )}
            {lastProductSync && (
              <span className="sync-time" title="Son stok senkronizasyonu">
                <Package size={14} /> {formatTimeAgo(lastProductSync)}
              </span>
            )}
          </div>
          <div
            onClick={() => setAutoSyncEnabled(prev => !prev)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'rgba(255,255,255,0.15)', padding: '6px 12px', borderRadius: '20px', cursor: 'pointer'
            }}
          >
            <div style={{
              width: '36px', height: '20px', borderRadius: '10px', position: 'relative', transition: 'all 0.3s',
              background: autoSyncEnabled ? '#4ade80' : 'rgba(255,255,255,0.3)', flexShrink: 0
            }}>
              <div style={{
                width: '16px', height: '16px', borderRadius: '50%', background: '#fff', position: 'absolute',
                top: '2px', left: autoSyncEnabled ? '18px' : '2px', transition: 'all 0.3s',
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
              }} />
            </div>
            <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#fff' }}>Otomatik Sync</span>
          </div>
          {newOrderCount > 0 && activeTab !== 'orders' && (
            <span className="new-order-badge" onClick={() => setActiveTab('orders')}>
              {newOrderCount} Yeni Sipariş
            </span>
          )}
        </div>
      </header>

      <div className="shopify-content">
        {/* Sidebar */}
        <aside className="shopify-sidebar">
          {/* Shopify Accordion */}
          <div className={`platform-group ${activePlatform === 'shopify' ? 'open' : ''}`}>
            <button
              className={`platform-header shopify-header-btn ${activePlatform === 'shopify' ? 'active' : ''}`}
              onClick={() => { if (activePlatform !== 'shopify') { setActivePlatform('shopify'); setActiveTab('products'); } }}
            >
              <img src="/marketplace-logos/shopify.png" alt="Shopify" style={{ width: '22px', height: '22px', borderRadius: '4px', objectFit: 'contain' }} />
              <span>Shopify</span>
              <svg className="chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <div className="platform-submenu">
              {tabs.map(tab => (
                <button
                  key={`shopify-${tab.id}`}
                  className={`shopify-tab ${activePlatform === 'shopify' && activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => { setActivePlatform('shopify'); setActiveTab(tab.id); }}
                >
                  <span className="tab-icon">{tab.icon}</span>
                  {tab.name}
                  {tab.id === 'orders' && newOrderCount > 0 && (
                    <span className="tab-badge">{newOrderCount}</span>
                  )}
                </button>
              ))}
              <button
                key="shopify-discounts"
                className={`shopify-tab ${activePlatform === 'shopify' && activeTab === 'discounts' ? 'active' : ''}`}
                onClick={() => { setActivePlatform('shopify'); setActiveTab('discounts'); }}
              >
                <span className="tab-icon"><Percent size={18} /></span>
                İndirim &amp; Adres
              </button>
            </div>
          </div>

          {/* Trendyol Accordion */}
          <div className={`platform-group ${activePlatform === 'trendyol' ? 'open' : ''}`}>
            <button
              className={`platform-header trendyol-header-btn ${activePlatform === 'trendyol' ? 'active' : ''}`}
              onClick={() => { if (activePlatform !== 'trendyol') { setActivePlatform('trendyol'); setActiveTab('products'); } }}
            >
              <img src="/marketplace-logos/trendyol.png" alt="Trendyol" style={{ width: '22px', height: '22px', borderRadius: '4px', objectFit: 'contain' }} />
              <span>Trendyol</span>
              <svg className="chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <div className="platform-submenu">
              {tabs.map(tab => (
                <button
                  key={`trendyol-${tab.id}`}
                  className={`shopify-tab ${activePlatform === 'trendyol' && activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => { setActivePlatform('trendyol'); setActiveTab(tab.id); }}
                >
                  <span className="tab-icon">{tab.icon}</span>
                  {tab.name}
                  {tab.id === 'orders' && trendyolNewOrderCount > 0 && (
                    <span className="tab-badge">{trendyolNewOrderCount}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Hepsiburada Accordion */}
          <div className={`platform-group ${activePlatform === 'hepsiburada' ? 'open' : ''}`}>
            <button
              className={`platform-header hepsiburada-header-btn ${activePlatform === 'hepsiburada' ? 'active' : ''}`}
              onClick={() => { if (activePlatform !== 'hepsiburada') { setActivePlatform('hepsiburada'); setActiveTab('products'); } }}
            >
              <img src="/marketplace-logos/hepsiburada.png" alt="Hepsiburada" style={{ width: '22px', height: '22px', borderRadius: '4px', objectFit: 'contain' }} />
              <span>Hepsiburada</span>
              <svg className="chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <div className="platform-submenu">
              {tabs.map(tab => (
                <button
                  key={`hepsiburada-${tab.id}`}
                  className={`shopify-tab ${activePlatform === 'hepsiburada' && activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => { setActivePlatform('hepsiburada'); setActiveTab(tab.id); }}
                >
                  <span className="tab-icon">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </div>
          </div>

          {/* Amazon Accordion */}
          <div className={`platform-group ${activePlatform === 'amazon' ? 'open' : ''}`}>
            <button
              className={`platform-header amazon-header-btn ${activePlatform === 'amazon' ? 'active' : ''}`}
              onClick={() => { if (activePlatform !== 'amazon') { setActivePlatform('amazon'); setActiveTab('products'); } }}
            >
              <img src="/marketplace-logos/amazon.png" alt="Amazon" style={{ width: '22px', height: '22px', borderRadius: '4px', objectFit: 'contain' }} />
              <span>Amazon</span>
              <svg className="chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <div className="platform-submenu">
              {tabs.map(tab => (
                <button
                  key={`amazon-${tab.id}`}
                  className={`shopify-tab ${activePlatform === 'amazon' && activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => { setActivePlatform('amazon'); setActiveTab(tab.id); }}
                >
                  <span className="tab-icon">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </div>
          </div>

          {/* N11 Accordion */}
          <div className={`platform-group ${activePlatform === 'n11' ? 'open' : ''}`}>
            <button
              className={`platform-header n11-header-btn ${activePlatform === 'n11' ? 'active' : ''}`}
              onClick={() => { if (activePlatform !== 'n11') { setActivePlatform('n11'); setActiveTab('products'); } }}
            >
              <img src="/marketplace-logos/n11.png" alt="N11" style={{ width: '22px', height: '22px', borderRadius: '4px', objectFit: 'contain' }} />
              <span>N11</span>
              <svg className="chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <div className="platform-submenu">
              {tabs.map(tab => (
                <button
                  key={`n11-${tab.id}`}
                  className={`shopify-tab ${activePlatform === 'n11' && activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => { setActivePlatform('n11'); setActiveTab(tab.id); }}
                >
                  <span className="tab-icon">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="shopify-main">
          {/* ===== NEW MARKETPLACE PANELS ===== */}
          {(activePlatform === 'hepsiburada' || activePlatform === 'amazon' || activePlatform === 'n11') && (
            <MarketplacePanel
              platform={activePlatform}
              activeTab={activeTab}
              user={user}
              selectedStore={selectedStore}
              stores={stores}
              showConfirmation={showConfirmation}
            />
          )}

          {/* ===== SHOPIFY CONTENT ===== */}
          {activePlatform === 'shopify' && <>
          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="shopify-panel">
              <h2>Shopify Bağlantı Ayarları</h2>

              {loadingSettings ? (
                <div className="loading">Yükleniyor...</div>
              ) : (
                <div className="settings-form">
                  {/* OAuth Connection Section */}
                  <div className="oauth-section">
                    <h3>Shopify Bağlantısı</h3>
                    {oauthStatus.loading ? (
                      <div className="oauth-loading">Bağlantı durumu kontrol ediliyor...</div>
                    ) : oauthStatus.connected ? (
                      <div className="oauth-connected">
                        <div className="oauth-status-badge connected">
                          <span className="status-dot"></span>
                          Bağlı
                        </div>
                        {oauthStatus.shop && (
                          <div className="oauth-shop-info">
                            <p><strong>Mağaza:</strong> {oauthStatus.shop.name}</p>
                            <p><strong>Domain:</strong> {oauthStatus.shop.domain}</p>
                          </div>
                        )}
                        <button
                          className="btn btn-danger"
                          onClick={disconnectOAuth}
                          disabled={disconnectingOAuth}
                        >
                          {disconnectingOAuth ? 'Bağlantı Kesiliyor...' : 'Bağlantıyı Kes'}
                        </button>
                      </div>
                    ) : (
                      <div className="oauth-disconnected">
                        <div className="oauth-status-badge disconnected">
                          <span className="status-dot"></span>
                          Bağlı Değil
                        </div>
                        <p className="oauth-description">
                          Shopify mağazanızı bağlamak için aşağıdaki butona tıklayın.
                          Shopify'a yönlendirilecek ve izinleri onaylamanız istenecek.
                        </p>
                        <button
                          className="btn btn-shopify"
                          onClick={initiateOAuth}
                          disabled={connectingOAuth}
                        >
                          {connectingOAuth ? 'Yönlendiriliyor...' : '🛍️ Shopify ile Bağlan'}
                        </button>
                      </div>
                    )}
                  </div>

                  <hr className="settings-divider" />

                  <div className="form-group">
                    <label>Shop URL</label>
                    <input
                      type="text"
                      placeholder="your-store.myshopify.com"
                      value={settings.shop_url}
                      onChange={(e) => setSettings({ ...settings, shop_url: e.target.value })}
                      disabled={oauthStatus.connected}
                    />
                    <small>Shopify mağazanızın URL'i (.myshopify.com ile biten)</small>
                  </div>

                  <div className="form-group">
                    <label>API Versiyonu</label>
                    <select
                      value={settings.api_version}
                      onChange={(e) => setSettings({ ...settings, api_version: e.target.value })}
                    >
                      <option value="2024-01">2024-01</option>
                      <option value="2024-04">2024-04</option>
                      <option value="2024-07">2024-07</option>
                      <option value="2024-10">2024-10</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Webhook Secret (Opsiyonel)</label>
                    <input
                      type="text"
                      placeholder="Webhook doğrulama anahtarı"
                      value={settings.webhook_secret}
                      onChange={(e) => setSettings({ ...settings, webhook_secret: e.target.value })}
                    />
                    <small>Webhook güvenliği için (Shopify Admin'den alınır)</small>
                  </div>

                  <div className="form-group">
                    <label>Varsayılan Stok Düşüm Mağazası</label>
                    <select
                      value={settings.default_deduction_store_id}
                      onChange={(e) => setSettings({ ...settings, default_deduction_store_id: e.target.value })}
                    >
                      <option value="">Mağaza Seçin</option>
                      {stores.map(store => (
                        <option key={store.id} value={store.id}>{store.name}</option>
                      ))}
                    </select>
                    <small>Shopify siparişlerinde stok bu mağazadan düşülür</small>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Mağaza Para Birimi</label>
                      <select
                        value={settings.store_currency}
                        onChange={(e) => setSettings({ ...settings, store_currency: e.target.value })}
                      >
                        <option value="USD">USD (Amerikan Doları)</option>
                        <option value="TRY">TRY (Türk Lirası)</option>
                        <option value="EUR">EUR (Euro)</option>
                      </select>
                      <small>Shopify mağazanızın para birimi</small>
                    </div>
                    <div className="form-group">
                      <label>TRY → USD Kuru</label>
                      <input
                        type="number"
                        step="0.0001"
                        value={settings.exchange_rate_try_to_usd}
                        onChange={(e) => setSettings({ ...settings, exchange_rate_try_to_usd: parseFloat(e.target.value) || 0 })}
                        placeholder="0.0286"
                      />
                      <small>1 TRY = {settings.exchange_rate_try_to_usd} USD</small>
                    </div>
                    <div className="form-group">
                      <label>Komisyon Oranı (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={settings.commission_percentage}
                        onChange={(e) => setSettings({ ...settings, commission_percentage: parseFloat(e.target.value) || 0 })}
                        placeholder="0"
                      />
                      <small>Shopify ürünlerine eklenecek komisyon yüzdesi (örn: %5 için 5 girin)</small>
                    </div>
                    <div className="form-group">
                      <label>Fiyat Değişim Eşiği (%)</label>
                      <input
                        type="number"
                        step="0.5"
                        min="0.5"
                        max="20"
                        value={settings.price_change_threshold}
                        onChange={(e) => setSettings({ ...settings, price_change_threshold: parseFloat(e.target.value) || 2 })}
                        placeholder="2"
                      />
                      <small>Altın veya dolar fiyatı bu yüzde kadar değiştiğinde Shopify fiyatları otomatik güncellenir</small>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Otomatik Fiyat Sync Aralığı (dakika)</label>
                    <div>
                      <select
                        value={settings.price_sync_interval}
                        onChange={(e) => setSettings({ ...settings, price_sync_interval: parseInt(e.target.value) || 0 })}
                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px' }}
                      >
                        <option value={0}>Kapalı</option>
                        <option value={30}>30 dakika</option>
                        <option value={60}>1 saat</option>
                        <option value={120}>2 saat</option>
                        <option value={180}>3 saat</option>
                        <option value={360}>6 saat</option>
                        <option value={720}>12 saat</option>
                        <option value={1440}>24 saat</option>
                      </select>
                      <small style={{ display: 'block', marginTop: '4px' }}>Tüm eşleşmiş ürünlerin fiyatlarını belirli aralıklarla otomatik olarak Shopify'a gönderir</small>
                    </div>
                  </div>

                  {settings.last_synced_gold_price && (
                    <div className="price-sync-info" style={{ background: '#f0f9ff', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#0369a1' }}>Son Fiyat Senkronizasyonu</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', fontSize: '13px' }}>
                        <div>
                          <span style={{ color: '#64748b' }}>Altın:</span>{' '}
                          <strong>₺{parseFloat(settings.last_synced_gold_price).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</strong>
                        </div>
                        <div>
                          <span style={{ color: '#64748b' }}>USD:</span>{' '}
                          <strong>₺{parseFloat(settings.last_synced_usd_rate).toFixed(4)}</strong>
                        </div>
                        <div>
                          <span style={{ color: '#64748b' }}>Tarih:</span>{' '}
                          <strong>{new Date(settings.last_price_sync_at).toLocaleString('tr-TR')}</strong>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="form-group toggle-group">
                    <label>
                      <span>Ürünleri Otomatik Yayınla</span>
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={settings.auto_publish}
                          onChange={(e) => setSettings({ ...settings, auto_publish: e.target.checked })}
                        />
                        <span className="slider"></span>
                      </label>
                    </label>
                    <small className="toggle-hint">Kapalı = Taslak olarak kaydet, Açık = Hemen yayınla</small>
                  </div>

                  <div className="form-group toggle-group">
                    <label>
                      <span>Sync Aktif</span>
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={settings.sync_enabled}
                          onChange={(e) => setSettings({ ...settings, sync_enabled: e.target.checked })}
                        />
                        <span className="slider"></span>
                      </label>
                    </label>
                  </div>

                  <div className="form-actions">
                    <button
                      className="btn btn-primary"
                      onClick={saveSettings}
                      disabled={savingSettings}
                    >
                      {savingSettings ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={testConnection}
                      disabled={testingConnection}
                    >
                      {testingConnection ? 'Test Ediliyor...' : 'Bağlantıyı Test Et'}
                    </button>
                  </div>

                  {connectionStatus && (
                    <div className={`connection-status ${connectionStatus.success ? 'success' : 'error'}`}>
                      <strong>{connectionStatus.success ? '✓' : '✗'} {connectionStatus.message}</strong>
                      {connectionStatus.shop && (
                        <div className="shop-info">
                          <p><strong>Mağaza:</strong> {connectionStatus.shop.name}</p>
                          <p><strong>E-posta:</strong> {connectionStatus.shop.email}</p>
                          <p><strong>Domain:</strong> {connectionStatus.shop.domain}</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="webhook-info">
                    <h3>Webhook URL'leri</h3>
                    <p>Shopify Admin panelinden aşağıdaki webhook'ları ekleyin:</p>
                    <div className="webhook-url">
                      <strong>Yeni Sipariş:</strong>
                      <code>{window.location.origin.replace(':3000', ':3001')}/api/shopify/webhooks/orders/create</code>
                    </div>
                    <div className="webhook-url">
                      <strong>İptal:</strong>
                      <code>{window.location.origin.replace(':3000', ':3001')}/api/shopify/webhooks/orders/cancelled</code>
                    </div>
                  </div>
                </div>
              )}

              {/* Multi-Location Stok Yönetimi */}
              <div className="settings-section" style={{ marginTop: '30px' }}>
                <h2 style={{ borderBottom: '2px solid #4f46e5', paddingBottom: '10px', marginBottom: '20px' }}>
                  📍 Multi-Location Stok Yönetimi
                </h2>
                <p style={{ color: '#666', marginBottom: '20px' }}>
                  Her şubenin stok miktarını Shopify'da ayrı ayrı görmek için şubeleri Shopify lokasyonlarıyla eşleştirin.
                </p>

                {/* Shopify Locations Yükle */}
                <div style={{ marginBottom: '20px' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={fetchShopifyLocations}
                    disabled={loadingLocations || !settings.shop_url}
                    style={{ marginRight: '10px' }}
                  >
                    {loadingLocations ? 'Yükleniyor...' : <><RefreshCw size={16} /> Shopify Lokasyonlarını Yükle</>}
                  </button>
                  {!settings.shop_url && (
                    <span style={{ color: '#ef4444', fontSize: '13px' }}>Önce Shopify bağlantısını yapın</span>
                  )}
                </div>

                {/* Shopify Locations Listesi */}
                {shopifyLocations.length > 0 && (
                  <div style={{ background: '#f0fdf4', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '10px', color: '#166534' }}>Shopify'daki Lokasyonlar ({shopifyLocations.length})</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {shopifyLocations.map(loc => (
                        <div key={loc.id} style={{
                          background: 'white',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #86efac',
                          fontSize: '13px'
                        }}>
                          <strong>{loc.name}</strong>
                          <span style={{ color: '#666', marginLeft: '8px' }}>ID: {loc.id}</span>
                          {loc.active && <span style={{ color: '#22c55e', marginLeft: '5px' }}>✓</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Şube-Location Eşlemeleri */}
                {storeLocationMappings.length > 0 && shopifyLocations.length > 0 && (
                  <div style={{ background: '#faf5ff', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '15px', color: '#7c3aed' }}>Şube → Shopify Lokasyon Eşleştirmesi</h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#ede9fe' }}>
                          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #c4b5fd' }}>Şube</th>
                          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #c4b5fd' }}>Shopify Lokasyonu</th>
                          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #c4b5fd' }}>Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {storeLocationMappings.map(store => (
                          <tr key={store.id} style={{ borderBottom: '1px solid #e9d5ff' }}>
                            <td style={{ padding: '12px', fontWeight: '500' }}>{store.name}</td>
                            <td style={{ padding: '12px' }}>
                              <select
                                value={store.shopify_location_id || ''}
                                onChange={(e) => updateStoreLocationMapping(store.id, e.target.value ? parseInt(e.target.value) : null)}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  border: '1px solid #c4b5fd',
                                  background: 'white'
                                }}
                              >
                                <option value="">-- Eşleştirilmemiş --</option>
                                {shopifyLocations.map(loc => (
                                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                                ))}
                              </select>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              {store.shopify_location_id ? (
                                <span style={{ color: '#22c55e', fontWeight: 'bold' }}>✓ Eşleşti</span>
                              ) : (
                                <span style={{ color: '#f59e0b' }}>⚠ Bekliyor</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                      <button
                        className="btn btn-primary"
                        onClick={saveStoreLocationMappings}
                        disabled={savingMappings}
                      >
                        {savingMappings ? 'Kaydediliyor...' : '💾 Eşlemeleri Kaydet'}
                      </button>
                      <button
                        className="btn btn-success"
                        onClick={syncInventoryMultiLocation}
                        disabled={syncingMultiLocation || storeLocationMappings.every(s => !s.shopify_location_id)}
                        style={{ background: '#22c55e' }}
                      >
                        {syncingMultiLocation ? 'Senkronize Ediliyor...' : '🔄 Stokları Shopify\'a Gönder'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Bilgi Kutusu */}
                <div style={{
                  background: '#fef3c7',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid #fcd34d'
                }}>
                  <h4 style={{ color: '#92400e', marginBottom: '10px' }}>ℹ️ Nasıl Çalışır?</h4>
                  <ol style={{ color: '#78350f', paddingLeft: '20px', lineHeight: '1.8' }}>
                    <li><strong>Shopify'da Lokasyon Oluşturun:</strong> Shopify Admin → Settings → Locations'dan her şube için lokasyon ekleyin</li>
                    <li><strong>Lokasyonları Yükleyin:</strong> "Shopify Lokasyonlarını Yükle" butonuna tıklayın</li>
                    <li><strong>Eşleştirin:</strong> Her şubeyi ilgili Shopify lokasyonuyla eşleştirin</li>
                    <li><strong>Kaydedin:</strong> Eşlemeleri kaydedin</li>
                    <li><strong>Senkronize Edin:</strong> "Stokları Shopify'a Gönder" ile şube bazlı stokları aktarın</li>
                  </ol>
                </div>
              </div>
            </div>
          )}

          {/* Products Tab */}
          {activeTab === 'products' && (
            <div className="shopify-panel">
              <div className="panel-header">
                <h2>Ürün Sync Yönetimi</h2>
                <div className="panel-actions">
                  {selectedProducts.length > 0 && (
                    <>
                      <button
                        className="btn btn-danger"
                        onClick={() => bulkDeleteProducts(false)}
                        disabled={bulkDeleting}
                        style={{ marginRight: '8px' }}
                      >
                        {bulkDeleting ? 'Siliniyor...' : <><Trash2 size={16} /> Seçilenleri Sil ({selectedProducts.length})</>}
                      </button>
                    </>
                  )}
                  <button
                    className="btn btn-danger"
                    onClick={() => bulkDeleteProducts(true)}
                    disabled={bulkDeleting}
                    style={{ marginRight: '8px', background: '#dc2626' }}
                  >
                    {bulkDeleting ? 'Siliniyor...' : <><Trash2 size={16} /> Tümünü Sil</>}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={syncAllProducts}
                    disabled={syncingProducts}
                  >
                    {syncingProducts ? 'Sync Ediliyor...' : <><Upload size={16} /> Tümünü Sync Et</>}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={syncPrices}
                    disabled={syncingPrices}
                    style={{ background: '#f59e0b', borderColor: '#f59e0b', color: '#fff' }}
                  >
                    {syncingPrices ? 'Güncelleniyor...' : <><Coins size={16} /> Fiyatları Güncelle</>}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={syncInventory}
                    disabled={syncingInventory}
                  >
                    {syncingInventory ? 'Güncelleniyor...' : <><BarChart3 size={16} /> Stokları Güncelle</>}
                  </button>
                </div>
              </div>

              {syncProgress && syncProgress.running && (() => {
                const { type, success, failed, total } = syncProgress;
                const percent = total > 0 ? Math.round(((success + failed) / total) * 100) : 0;
                const typeLabel = type === 'price' ? 'Fiyat' : type === 'inventory' ? 'Stok' : 'Urun';
                return (
                  <div className="sync-progress-bar">
                    <div className="sync-progress-header">
                      <span>{typeLabel} Senkronizasyonu</span>
                      <span>{success + failed} / {total}</span>
                    </div>
                    <div className="progress-track">
                      <div className="progress-fill" style={{ width: `${percent}%` }} />
                    </div>
                    <div className="sync-progress-stats">
                      <span className="success">&#10003; {success} basarili</span>
                      <span className="failed">&#10007; {failed} hata</span>
                      <span className="percent">%{percent}</span>
                    </div>
                  </div>
                );
              })()}

              <div className="filter-bar">
                <input
                  type="text"
                  placeholder="Ürün ara (isim veya barkod)..."
                  value={productFilter}
                  onChange={(e) => setProductFilter(e.target.value)}
                />
              </div>

              {loadingProducts ? (
                <div className="loading">Yükleniyor...</div>
              ) : (
                <div className="table-container">
                  <table className="data-table products-table">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={selectedProducts.length > 0 && selectedProducts.length === filteredProducts.filter(p => p.shopify_product_id).length}
                            onChange={toggleSelectAll}
                            disabled={filteredProducts.filter(p => p.shopify_product_id).length === 0}
                            style={{ cursor: 'pointer' }}
                          />
                        </th>
                        <th className="photo-column">Fotoğraf</th>
                        <th>Ürün</th>
                        <th>Barkod</th>
                        <th>Fiyat</th>
                        <th>Stok</th>
                        <th>Sync Durumu</th>
                        <th>Son Sync</th>
                        <th>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map(product => (
                        <tr key={product.id}>
                          <td>
                            {product.shopify_product_id && (
                              <input
                                type="checkbox"
                                checked={selectedProducts.includes(product.id)}
                                onChange={() => toggleProductSelection(product.id)}
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                          </td>
                          <td className="photo-cell">
                            {product.image ? (
                              <img
                                src={`${API_URL.replace('/api', '')}${product.image.startsWith('/') ? product.image : '/uploads/' + product.image}`}
                                alt={product.name}
                                className="product-photo"
                                loading="lazy"
                                onClick={() => setImagePopup({
                                  show: true,
                                  src: `${API_URL.replace('/api', '')}${product.image.startsWith('/') ? product.image : '/uploads/' + product.image}`,
                                  name: product.name
                                })}
                              />
                            ) : (
                              <div className="no-photo">
                                <Package size={24} />
                              </div>
                            )}
                          </td>
                          <td className="product-name-cell">{product.name}</td>
                          <td>{product.barcode || '-'}</td>
                          <td>{formatCurrency(product.sell_price, product.currency)}</td>
                          <td>{product.total_stock || 0}</td>
                          <td>{getSyncStatusBadge(product.sync_status)}</td>
                          <td>{formatDate(product.last_synced_at)}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-icon btn-sync"
                                onClick={() => syncProduct(product.id)}
                                title="Sync Et"
                              >
                                <RefreshCw size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredProducts.length === 0 && (
                    <div className="empty-state">
                      E-commerce aktif ürün bulunamadı
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="shopify-panel orders-panel">
              <div className="panel-header">
                <h2>Shopify Siparişleri</h2>
                <div className="panel-actions">
                  {selectedOrders.length > 0 && (
                    <>
                      <button
                        className="btn btn-success"
                        onClick={() => bulkProcessOrders(false)}
                        disabled={bulkProcessing || bulkDeletingOrders}
                        style={{ marginRight: '8px' }}
                      >
                        {bulkProcessing ? 'İşleniyor...' : <>✓ Seçilenleri İşle ({selectedOrders.length})</>}
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => bulkDeleteOrders(false)}
                        disabled={bulkProcessing || bulkDeletingOrders}
                        style={{ marginRight: '8px' }}
                      >
                        {bulkDeletingOrders ? 'Siliniyor...' : <><Trash2 size={16} /> Seçilenleri Sil ({selectedOrders.length})</>}
                      </button>
                    </>
                  )}
                  {orders.filter(o => o.sync_status === 'pending' || o.sync_status === 'error').length > 0 && (
                    <button
                      className="btn btn-success"
                      onClick={() => bulkProcessOrders(true)}
                      disabled={bulkProcessing || bulkDeletingOrders}
                      style={{ marginRight: '8px' }}
                    >
                      {bulkProcessing ? 'İşleniyor...' : <>✓ Tümünü İşle ({orders.filter(o => o.sync_status === 'pending' || o.sync_status === 'error').length})</>}
                    </button>
                  )}
                  <button
                    className="btn btn-danger"
                    onClick={() => bulkDeleteOrders(true)}
                    disabled={bulkProcessing || bulkDeletingOrders}
                    style={{ marginRight: '8px', background: '#dc2626' }}
                  >
                    {bulkDeletingOrders ? 'Siliniyor...' : <><Trash2 size={16} /> Tüm Siparişleri Sil</>}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={pullOrders}
                    disabled={pullingOrders}
                  >
                    {pullingOrders ? 'Çekiliyor...' : <><Download size={16} /> Siparişleri Çek</>}
                  </button>
                </div>
              </div>

              {/* Stats Bar */}
              <div className="orders-stats-bar">
                <div className="order-stat total">
                  <span className="stat-value">{orders.length}</span>
                  <span className="stat-label">Toplam</span>
                </div>
                <div className="order-stat pending">
                  <span className="stat-value">{orders.filter(o => o.sync_status === 'pending').length}</span>
                  <span className="stat-label">Bekleyen</span>
                </div>
                <div className="order-stat synced">
                  <span className="stat-value">{orders.filter(o => o.sync_status === 'synced').length}</span>
                  <span className="stat-label">İşlenen</span>
                </div>
                <div className="order-stat error">
                  <span className="stat-value">{orders.filter(o => o.sync_status === 'error').length}</span>
                  <span className="stat-label">Hata</span>
                </div>
                <div className="order-stat revenue">
                  <span className="stat-value">{formatCurrency(orders.reduce((sum, o) => sum + (parseFloat(o.total_price) || 0), 0))}</span>
                  <span className="stat-label">Toplam Gelir</span>
                </div>
              </div>

              {/* Filters */}
              <div className="filter-bar orders-filter-bar">
                <input
                  type="text"
                  placeholder="Sipariş no, müşteri adı veya email ara..."
                  className="search-input"
                  onChange={(e) => setOrderSearchQuery(e.target.value.toLowerCase())}
                />
                <select value={orderFilter} onChange={(e) => setOrderFilter(e.target.value)}>
                  <option value="all">Tüm Durumlar</option>
                  <option value="pending">Bekleyen</option>
                  <option value="processing">İşleniyor</option>
                  <option value="synced">İşlenmiş</option>
                  <option value="error">Hatalı</option>
                </select>
              </div>

              {loadingOrders ? (
                <div className="loading">Yükleniyor...</div>
              ) : (
                <div className="table-container">
                  <table className="data-table orders-table">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={selectedOrders.length > 0 && selectedOrders.length === filteredOrders.length}
                            onChange={toggleSelectAllOrders}
                            disabled={filteredOrders.length === 0}
                            style={{ cursor: 'pointer' }}
                          />
                        </th>
                        <th>Sipariş No</th>
                        <th>Müşteri</th>
                        <th>Ürünler</th>
                        <th>Tutar</th>
                        <th>Ödeme</th>
                        <th>Kargo</th>
                        <th>ERP Durumu</th>
                        <th>Tarih</th>
                        <th>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map(order => {
                        const items = (() => {
                          try {
                            return typeof order.order_items === 'string' ? JSON.parse(order.order_items) : (order.order_items || []);
                          } catch { return []; }
                        })();
                        return (
                          <tr key={order.id} className={order.sync_status === 'pending' ? 'pending-row' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedOrders.includes(order.id)}
                                onChange={() => toggleOrderSelection(order.id)}
                                style={{ cursor: 'pointer' }}
                              />
                            </td>
                            <td>
                              <strong>#{order.shopify_order_number}</strong>
                              <small style={{display: 'block', color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem'}}>ID: {order.shopify_order_id}</small>
                            </td>
                            <td>
                              <div style={{fontWeight: 500}}>{order.customer_name || 'Misafir'}</div>
                              <small style={{color: 'rgba(255,255,255,0.5)'}}>{order.customer_email || '-'}</small>
                            </td>
                            <td>{items.length} ürün</td>
                            <td style={{color: '#00bfa6', fontWeight: 600}}>{formatCurrency(order.total_price)}</td>
                            <td>
                              <span className={`payment-status ${order.financial_status === 'paid' ? 'paid' : ''}`}>
                                {order.financial_status === 'paid' ? 'Ödendi' : order.financial_status === 'pending' ? 'Bekliyor' : order.financial_status || '-'}
                              </span>
                            </td>
                            <td>
                              <span className={`fulfillment-status ${order.fulfillment_status === 'fulfilled' ? 'fulfilled' : ''}`}>
                                {order.fulfillment_status === 'fulfilled' ? 'Gönderildi' : order.fulfillment_status === 'unfulfilled' ? 'Hazırlanmadı' : order.fulfillment_status || 'Bekliyor'}
                              </span>
                            </td>
                            <td>
                              {getSyncStatusBadge(order.sync_status)}
                              {order.erp_sale_id && <small style={{display: 'block', color: '#4ade80', marginTop: '4px'}}>ERP #{order.erp_sale_id}</small>}
                            </td>
                            <td style={{fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)'}}>{formatDate(order.shopify_created_at)}</td>
                            <td>
                              <div style={{display: 'flex', gap: '6px'}}>
                                {(order.sync_status === 'pending' || order.sync_status === 'error') && (
                                  <button
                                    className="btn btn-small btn-primary"
                                    onClick={() => processOrder(order.id)}
                                  >
                                    İşle
                                  </button>
                                )}
                                <button
                                  className="btn btn-small btn-secondary"
                                  onClick={() => setSelectedOrder(order)}
                                >
                                  Detay
                                </button>
                              </div>
                              {order.sync_error && (
                                <small style={{display: 'block', color: '#f43f5e', marginTop: '4px'}} title={order.sync_error}>⚠️ {order.sync_error.substring(0, 30)}...</small>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {orders.length === 0 && (
                    <div className="empty-state">
                      <Package size={48} style={{ color: '#9ca3af', marginBottom: '15px' }} />
                      <h3>Sipariş bulunamadı</h3>
                      <p>Siparişleri çekmek için yukarıdaki butonu kullanın.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Order Detail Modal */}
              {selectedOrder && (
                <div className={`order-detail-modal-overlay ${closingModal === 'order' ? 'closing' : ''}`}>
                  <div className="order-detail-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="order-modal-header">
                      <h3>Sipariş #{selectedOrder.shopify_order_number}</h3>
                      <button className="close-btn" onClick={() => { setClosingModal('order'); setTimeout(() => { setSelectedOrder(null); setClosingModal(null); }, 250); }}>✕</button>
                    </div>
                    <div className="order-modal-content">
                      <div className="order-info-grid">
                        <div className="info-section">
                          <h4>Sipariş Bilgileri</h4>
                          <p><span>Shopify ID:</span> <strong>{selectedOrder.shopify_order_id}</strong></p>
                          <p><span>Tarih:</span> <strong>{formatDate(selectedOrder.shopify_created_at)}</strong></p>
                          <p><span>Durum:</span> {getSyncStatusBadge(selectedOrder.sync_status)}</p>
                          {selectedOrder.erp_sale_id && <p><span>ERP Satış ID:</span> <strong>#{selectedOrder.erp_sale_id}</strong></p>}
                          {selectedOrder.sync_error && <p className="error-text"><span>Hata:</span> {selectedOrder.sync_error}</p>}
                        </div>
                        <div className="info-section">
                          <h4>Müşteri Bilgileri</h4>
                          <p><span>Ad Soyad:</span> <strong>{selectedOrder.customer_name || 'Misafir'}</strong></p>
                          <p><span>Email:</span> <strong>{selectedOrder.customer_email || '-'}</strong></p>
                          {selectedOrder.erp_customer_id && <p><span>ERP Müşteri ID:</span> <strong>#{selectedOrder.erp_customer_id}</strong></p>}
                        </div>
                        <div className="info-section">
                          <h4>Ödeme & Kargo</h4>
                          <p><span>Ödeme:</span> <strong>{selectedOrder.financial_status === 'paid' ? 'Ödendi' : selectedOrder.financial_status || '-'}</strong></p>
                          <p><span>Kargo:</span> <strong>{selectedOrder.fulfillment_status === 'fulfilled' ? 'Gönderildi' : selectedOrder.fulfillment_status || 'Bekliyor'}</strong></p>
                          <p className="total-row"><span>Toplam:</span> <strong>{formatCurrency(selectedOrder.total_price)}</strong></p>
                        </div>
                      </div>
                      <div className="order-items-section">
                        <h4>Sipariş Ürünleri</h4>
                        <table className="items-table">
                          <thead>
                            <tr>
                              <th>Ürün</th>
                              <th>SKU</th>
                              <th>Adet</th>
                              <th>Birim Fiyat</th>
                              <th>Toplam</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              try {
                                const items = typeof selectedOrder.order_items === 'string' ? JSON.parse(selectedOrder.order_items) : (selectedOrder.order_items || []);
                                return items.map((item, idx) => (
                                  <tr key={idx}>
                                    <td>{item.title || item.name}</td>
                                    <td>{item.sku || '-'}</td>
                                    <td>{item.quantity}</td>
                                    <td>{formatCurrency(item.price)}</td>
                                    <td>{formatCurrency((item.price || 0) * (item.quantity || 1))}</td>
                                  </tr>
                                ));
                              } catch { return <tr><td colSpan="5">Ürün bilgisi yüklenemedi</td></tr>; }
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="order-modal-footer">
                      {(selectedOrder.sync_status === 'pending' || selectedOrder.sync_status === 'error') && (
                        <button className="btn btn-primary" onClick={() => { processOrder(selectedOrder.id); setSelectedOrder(null); }}>
                          ERP'ye İşle
                        </button>
                      )}
                      <button className="btn btn-secondary" onClick={() => setSelectedOrder(null)}>Kapat</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Customers Tab */}
          {activeTab === 'customers' && (
            <div className="shopify-panel customers-panel">
              <div className="panel-header">
                <h2>Shopify Müşterileri</h2>
                <div className="panel-actions">
                  <button
                    className="btn btn-primary"
                    onClick={fetchCustomers}
                    disabled={loadingCustomers}
                  >
                    {loadingCustomers ? 'Yükleniyor...' : <><RefreshCw size={16} /> Yenile</>}
                  </button>
                </div>
              </div>

              {/* Stats Bar */}
              <div className="orders-stats-bar">
                <div className="order-stat total">
                  <span className="stat-value">{customers.length}</span>
                  <span className="stat-label">Toplam Müşteri</span>
                </div>
                <div className="order-stat synced">
                  <span className="stat-value">{customers.filter(c => c.orders_count > 0).length}</span>
                  <span className="stat-label">Sipariş Veren</span>
                </div>
                <div className="order-stat pending">
                  <span className="stat-value">{customers.filter(c => c.accepts_marketing).length}</span>
                  <span className="stat-label">Pazarlama İzni</span>
                </div>
                <div className="order-stat revenue">
                  <span className="stat-value">{formatCurrency(customers.reduce((sum, c) => sum + (parseFloat(c.total_spent) || 0), 0))}</span>
                  <span className="stat-label">Toplam Harcama</span>
                </div>
              </div>

              {/* Search */}
              <div className="filter-bar">
                <input
                  type="text"
                  placeholder="Müşteri adı, email veya telefon ara..."
                  className="search-input"
                  value={customerSearchQuery}
                  onChange={(e) => setCustomerSearchQuery(e.target.value.toLowerCase())}
                  style={{ flex: 1, maxWidth: '400px' }}
                />
              </div>

              {loadingCustomers ? (
                <div className="loading">Yükleniyor...</div>
              ) : (
                <div className="table-container">
                  <table className="data-table customers-table">
                    <thead>
                      <tr>
                        <th>Müşteri</th>
                        <th>Email</th>
                        <th>Telefon</th>
                        <th>Sipariş Sayısı</th>
                        <th>Toplam Harcama</th>
                        <th>Pazarlama</th>
                        <th>Kayıt Tarihi</th>
                        <th>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers
                        .filter(customer => {
                          if (!customerSearchQuery) return true;
                          return (
                            customer.full_name?.toLowerCase().includes(customerSearchQuery) ||
                            customer.email?.toLowerCase().includes(customerSearchQuery) ||
                            customer.phone?.includes(customerSearchQuery)
                          );
                        })
                        .map(customer => (
                          <tr key={customer.id}>
                            <td>
                              <div style={{ fontWeight: 600 }}>{customer.full_name}</div>
                              {customer.tags && <small style={{ color: '#64748b' }}>{customer.tags}</small>}
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {customer.email || '-'}
                                {customer.verified_email && <span title="Doğrulanmış" style={{ color: '#10b981' }}>✓</span>}
                              </div>
                            </td>
                            <td>{customer.phone || '-'}</td>
                            <td>
                              <span className={`order-count ${customer.orders_count > 0 ? 'has-orders' : ''}`}>
                                {customer.orders_count}
                              </span>
                            </td>
                            <td style={{ color: '#00bfa6', fontWeight: 600 }}>
                              {formatCurrency(customer.total_spent)}
                            </td>
                            <td>
                              <span className={`marketing-status ${customer.accepts_marketing ? 'accepted' : 'declined'}`}>
                                {customer.accepts_marketing ? 'Evet' : 'Hayır'}
                              </span>
                            </td>
                            <td style={{ fontSize: '0.85rem', color: '#64748b' }}>
                              {formatDate(customer.created_at)}
                            </td>
                            <td>
                              <button
                                className="btn btn-small btn-secondary"
                                onClick={() => fetchCustomerDetail(customer.id)}
                              >
                                Detay
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {customers.length === 0 && (
                    <div className="empty-state">
                      <Users size={48} style={{ color: '#9ca3af', marginBottom: '15px' }} />
                      <h3>Müşteri bulunamadı</h3>
                      <p>Shopify mağazanızda henüz müşteri yok veya bağlantı ayarlarını kontrol edin.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Customer Detail Modal */}
              {selectedCustomer && (
                <div className={`order-detail-modal-overlay ${closingModal === 'customer' ? 'closing' : ''}`}>
                  <div className="order-detail-modal customer-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="order-modal-header">
                      <h3><User size={20} style={{ marginRight: '8px' }} /> {selectedCustomer.full_name}</h3>
                      <button className="close-btn" onClick={() => { setClosingModal('customer'); setTimeout(() => { setSelectedCustomer(null); setCustomerOrders([]); setClosingModal(null); }, 250); }}>✕</button>
                    </div>
                    {loadingCustomerDetail ? (
                      <div className="loading" style={{ padding: '40px' }}>Yükleniyor...</div>
                    ) : (
                      <div className="order-modal-content">
                        <div className="order-info-grid">
                          <div className="info-section">
                            <h4>İletişim Bilgileri</h4>
                            <p><span>Email:</span> <strong>{selectedCustomer.email || '-'}</strong></p>
                            <p><span>Telefon:</span> <strong>{selectedCustomer.phone || '-'}</strong></p>
                            <p><span>Email Doğrulandı:</span> <strong>{selectedCustomer.verified_email ? 'Evet ✓' : 'Hayır'}</strong></p>
                            <p><span>Pazarlama İzni:</span> <strong>{selectedCustomer.accepts_marketing ? 'Evet' : 'Hayır'}</strong></p>
                          </div>
                          <div className="info-section">
                            <h4>Sipariş Özeti</h4>
                            <p><span>Toplam Sipariş:</span> <strong>{selectedCustomer.orders_count || 0}</strong></p>
                            <p><span>Toplam Harcama:</span> <strong style={{ color: '#00bfa6' }}>{formatCurrency(selectedCustomer.total_spent)}</strong></p>
                            <p><span>Son Sipariş:</span> <strong>{selectedCustomer.last_order_name || '-'}</strong></p>
                            <p><span>Kayıt Tarihi:</span> <strong>{formatDate(selectedCustomer.created_at)}</strong></p>
                          </div>
                          {selectedCustomer.default_address && (
                            <div className="info-section">
                              <h4>Varsayılan Adres</h4>
                              <p><span>Adres:</span> <strong>{selectedCustomer.default_address.address1 || '-'}</strong></p>
                              {selectedCustomer.default_address.address2 && <p><span></span> <strong>{selectedCustomer.default_address.address2}</strong></p>}
                              <p><span>Şehir:</span> <strong>{selectedCustomer.default_address.city || '-'}</strong></p>
                              <p><span>Ülke:</span> <strong>{selectedCustomer.default_address.country || '-'}</strong></p>
                              <p><span>Posta Kodu:</span> <strong>{selectedCustomer.default_address.zip || '-'}</strong></p>
                            </div>
                          )}
                        </div>

                        {selectedCustomer.note && (
                          <div className="customer-note">
                            <h4>Not</h4>
                            <p>{selectedCustomer.note}</p>
                          </div>
                        )}

                        {customerOrders.length > 0 && (
                          <div className="order-items-section">
                            <h4>Sipariş Geçmişi ({customerOrders.length})</h4>
                            <table className="items-table">
                              <thead>
                                <tr>
                                  <th>Sipariş No</th>
                                  <th>Tarih</th>
                                  <th>Tutar</th>
                                  <th>Ödeme</th>
                                  <th>Kargo</th>
                                </tr>
                              </thead>
                              <tbody>
                                {customerOrders.map(order => (
                                  <tr key={order.id}>
                                    <td><strong>{order.name}</strong></td>
                                    <td>{formatDate(order.created_at)}</td>
                                    <td style={{ color: '#00bfa6', fontWeight: 600 }}>{formatCurrency(order.total_price)}</td>
                                    <td>
                                      <span className={`payment-status ${order.financial_status === 'paid' ? 'paid' : ''}`}>
                                        {order.financial_status === 'paid' ? 'Ödendi' : order.financial_status || '-'}
                                      </span>
                                    </td>
                                    <td>
                                      <span className={`fulfillment-status ${order.fulfillment_status === 'fulfilled' ? 'fulfilled' : ''}`}>
                                        {order.fulfillment_status === 'fulfilled' ? 'Gönderildi' : order.fulfillment_status || 'Bekliyor'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="order-modal-footer">
                      <button className="btn btn-secondary" onClick={() => { setSelectedCustomer(null); setCustomerOrders([]); }}>Kapat</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Logs Tab */}
          {activeTab === 'logs' && (
            <div className="shopify-panel">
              <h2>Sync Logları</h2>

              <div className="filter-bar">
                <select
                  value={logFilter.type}
                  onChange={(e) => setLogFilter({ ...logFilter, type: e.target.value })}
                >
                  <option value="">Tüm Tipler</option>
                  <option value="product_push">Ürün Push</option>
                  <option value="product_update">Ürün Güncelleme</option>
                  <option value="product_delete">Ürün Silme</option>
                  <option value="inventory_update">Stok Güncelleme</option>
                  <option value="order_pull">Sipariş Çekme</option>
                  <option value="webhook">Webhook</option>
                </select>
                <select
                  value={logFilter.status}
                  onChange={(e) => setLogFilter({ ...logFilter, status: e.target.value })}
                >
                  <option value="">Tüm Durumlar</option>
                  <option value="success">Başarılı</option>
                  <option value="error">Hata</option>
                  <option value="warning">Uyarı</option>
                </select>
              </div>

              {loadingLogs ? (
                <div className="loading">Yükleniyor...</div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Tarih</th>
                        <th>Tip</th>
                        <th>Durum</th>
                        <th>Mesaj</th>
                        <th>Entity ID</th>
                        <th>Shopify ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map(log => (
                        <tr key={log.id}>
                          <td>{formatDate(log.created_at)}</td>
                          <td>
                            <span className="log-type">{log.sync_type}</span>
                          </td>
                          <td>
                            <span className={`log-status ${log.status}`}>{log.status}</span>
                          </td>
                          <td className="log-message">{log.message}</td>
                          <td>{log.entity_id || '-'}</td>
                          <td>{log.shopify_id || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {logs.length === 0 && (
                    <div className="empty-state">Log bulunamadı</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* İndirim & Adres Tab */}
          {activeTab === 'discounts' && (
            <div className="shopify-panel">
              <h2>Toplu İndirim & Ürün Adresleri</h2>
              <p style={{ color: 'var(--text-secondary, #6b7280)', marginTop: '-8px', marginBottom: '16px' }}>
                İndirimler doğrudan burada uygulanır ve Shopify'a giden canlı fiyat hesabına dahil edilir —
                otomatik fiyat senkronizasyonuyla çakışmaz.
              </p>

              <div className="filter-bar" style={{ marginBottom: '20px' }}>
                <button
                  className={`btn ${discountSubTab === 'campaigns' ? 'btn-primary' : ''}`}
                  onClick={() => setDiscountSubTab('campaigns')}
                >
                  <Percent size={16} style={{ marginRight: '6px' }} /> İndirim Kampanyaları
                </button>
                <button
                  className={`btn ${discountSubTab === 'handles' ? 'btn-primary' : ''}`}
                  onClick={() => setDiscountSubTab('handles')}
                >
                  <Link2 size={16} style={{ marginRight: '6px' }} /> Ürün Adresleri
                </button>
              </div>

              {pushStatus?.running && (
                <div className="settings-form" style={{ marginBottom: '20px', padding: '14px', background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '0.9rem' }}>
                    <span>
                      <RefreshCw size={14} className="spin" style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      <strong>{pushStatus.campaignName}</strong> Shopify'a {pushStatus.action === 'apply' ? 'uygulanıyor' : pushStatus.action === 'pause' ? 'geri alınıyor (duraklatma)' : pushStatus.action === 'resume' ? 'yeniden uygulanıyor' : 'geri alınıyor (sonlandırma)'}...
                    </span>
                    <span>{pushStatus.done} / {pushStatus.total} ürün ({pushStatus.total > 0 ? Math.round((pushStatus.done / pushStatus.total) * 100) : 0}%)</span>
                  </div>
                  <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', background: '#0d9488', transition: 'width 0.3s',
                      width: `${pushStatus.total > 0 ? (pushStatus.done / pushStatus.total) * 100 : 0}%`
                    }} />
                  </div>
                  {pushStatus.failed > 0 && (
                    <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#b45309' }}>{pushStatus.failed} üründe hata oluştu</div>
                  )}
                </div>
              )}

              {discountSubTab === 'campaigns' && (
                <>
                  <div className="settings-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Kampanya Adı (opsiyonel)</label>
                        <input type="text" value={discountForm.name}
                          onChange={e => setDiscountForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Örn: Yaz İndirimi" />
                      </div>
                      <div className="form-group">
                        <label>Hedef</label>
                        <select value={discountForm.scopeType}
                          onChange={e => setDiscountForm(prev => ({ ...prev, scopeType: e.target.value }))}>
                          <option value="all">Tüm Ürünler</option>
                          <option value="category">Kategoriye Göre (ERP)</option>
                          <option value="shopify_collection">Shopify Koleksiyonuna Göre</option>
                          <option value="brand">Markaya Göre</option>
                          <option value="products">Seçili Ürünler</option>
                        </select>
                      </div>
                    </div>

                    {discountForm.scopeType === 'shopify_collection' && (
                      <div className="form-row">
                        <div className="form-group">
                          <label>Shopify Koleksiyonu {loadingCollections && '(yükleniyor...)'}</label>
                          <select value={discountForm.collectionId}
                            onChange={e => {
                              const sel = shopifyCollections.find(c => c.id === e.target.value);
                              setDiscountForm(prev => ({ ...prev, collectionId: e.target.value, collectionTitle: sel?.title || '' }));
                            }}>
                            <option value="">Seçin...</option>
                            {shopifyCollections.map(c => (
                              <option key={c.id} value={c.id}>{c.title} ({c.count} ürün)</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {discountForm.scopeType === 'category' && (
                      <div className="form-row">
                        <div className="form-group">
                          <label>Kategori</label>
                          <select value={discountForm.category}
                            onChange={e => setDiscountForm(prev => ({ ...prev, category: e.target.value }))}>
                            <option value="">Seçin...</option>
                            {productCategories.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      </div>
                    )}

                    {discountForm.scopeType === 'brand' && (
                      <div className="form-row">
                        <div className="form-group">
                          <label>Marka</label>
                          <select value={discountForm.brand}
                            onChange={e => setDiscountForm(prev => ({ ...prev, brand: e.target.value }))}>
                            <option value="">Seçin...</option>
                            {productBrands.map(b => <option key={b} value={b}>{b}</option>)}
                          </select>
                        </div>
                      </div>
                    )}

                    {discountForm.scopeType === 'products' && (
                      <div className="form-row" style={{ position: 'relative' }}>
                        <div className="form-group" style={{ flex: 1 }}>
                          <label>Ürün Ara (isim / barkod)</label>
                          <input type="text" value={discountProductSearch}
                            onChange={e => setDiscountProductSearch(e.target.value)}
                            placeholder="En az 2 karakter yazın..." />
                          {discountProductResults.length > 0 && (
                            <div className="product-picker-results">
                              {discountProductResults.map(p => (
                                <div key={p.id} className="product-picker-row"
                                  onClick={() => {
                                    if (!discountSelectedProducts.find(sp => sp.id === p.id)) {
                                      setDiscountSelectedProducts(prev => [...prev, { id: p.id, name: p.name, barcode: p.barcode }]);
                                    }
                                    setDiscountProductSearch('');
                                    setDiscountProductResults([]);
                                  }}>
                                  {p.name} {p.barcode ? `(${p.barcode})` : ''}
                                </div>
                              ))}
                            </div>
                          )}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                            {discountSelectedProducts.map(p => (
                              <span key={p.id} className="product-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'default' }}>
                                {p.name}
                                <X size={12} style={{ cursor: 'pointer' }}
                                  onClick={() => setDiscountSelectedProducts(prev => prev.filter(sp => sp.id !== p.id))} />
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="form-row">
                      <div className="form-group">
                        <label>İndirim Türü</label>
                        <select value={discountForm.discountType}
                          onChange={e => setDiscountForm(prev => ({ ...prev, discountType: e.target.value }))}>
                          <option value="percent">Yüzde (%)</option>
                          <option value="amount">Tutar</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Değer</label>
                        <input type="number" min="0" step="0.01" value={discountForm.discountValue}
                          onChange={e => setDiscountForm(prev => ({ ...prev, discountValue: e.target.value }))}
                          placeholder={discountForm.discountType === 'percent' ? 'Örn: 20' : 'Örn: 100'} />
                      </div>
                      <div className="form-group">
                        <label>Yuvarlama</label>
                        <select value={discountForm.rounding}
                          onChange={e => setDiscountForm(prev => ({ ...prev, rounding: e.target.value }))}>
                          <option value="none">Yok</option>
                          <option value="integer">Tam Sayı</option>
                          <option value="end90">,90 ile bitsin</option>
                          <option value="end99">,99 ile bitsin</option>
                        </select>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <button className="btn" disabled={previewingDiscount} onClick={handlePreviewDiscount}>
                        <Search size={16} style={{ marginRight: '6px' }} />
                        {previewingDiscount ? 'Hesaplanıyor...' : 'Önizle'}
                      </button>
                      <button className="btn btn-primary" disabled={applyingDiscount} onClick={handleApplyDiscount}>
                        {applyingDiscount ? 'Uygulanıyor...' : 'İndirimi Uygula'}
                      </button>
                    </div>

                    {discountPreview && (
                      <div className="table-container" style={{ marginTop: '16px' }}>
                        <p>
                          Hedefte <strong>{discountPreview.productCount}</strong> ürün var,
                          <strong> {discountPreview.affectedCount}</strong> tanesi indirimden etkilenecek.
                          {discountPreview.skippedActive > 0 && ` ${discountPreview.skippedActive} ürün zaten başka aktif bir kampanyada (atlanacak).`}
                        </p>
                        <table className="data-table">
                          <thead>
                            <tr><th>Ürün</th><th>Eski Fiyat</th><th>Yeni Fiyat</th><th>Üstü Çizili</th></tr>
                          </thead>
                          <tbody>
                            {discountPreview.sample.map(item => (
                              <tr key={item.productId}>
                                <td>{item.productName}</td>
                                <td>{item.origPrice}</td>
                                <td>{item.newPrice}</td>
                                <td>{item.compareAtPrice}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <hr className="settings-divider" />

                  <h3>Kampanyalar</h3>
                  {loadingDiscounts ? (
                    <div className="loading">Yükleniyor...</div>
                  ) : (
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Ad</th><th>Hedef</th><th>İndirim</th><th>Etkilenen Ürün</th><th>Durum</th><th>İşlemler</th>
                          </tr>
                        </thead>
                        <tbody>
                          {discountCampaigns.map(c => (
                            <tr key={c.id}>
                              <td>{c.name}</td>
                              <td>
                                {c.scope_type === 'all' && 'Tüm Ürünler'}
                                {c.scope_type === 'category' && `Kategori: ${c.scope_value?.category}`}
                                {c.scope_type === 'brand' && `Marka: ${c.scope_value?.brand}`}
                                {c.scope_type === 'products' && `${c.scope_value?.product_ids?.length || 0} seçili ürün`}
                                {c.scope_type === 'shopify_collection' && `Koleksiyon: ${c.scope_value?.collection_title || '-'}`}
                              </td>
                              <td>{c.discount_type === 'percent' ? `%${c.discount_value}` : `${c.discount_value} TL`}</td>
                              <td>{c.affected_product_count}</td>
                              <td><span className={`log-status ${c.status === 'active' ? 'success' : c.status === 'paused' ? 'warning' : 'error'}`}>{c.status}</span></td>
                              <td>
                                {c.status === 'active' && (
                                  <button className="btn" onClick={() => handlePauseCampaign(c.id)} title="Duraklat">
                                    <Pause size={14} />
                                  </button>
                                )}
                                {c.status === 'paused' && (
                                  <button className="btn" onClick={() => handleResumeCampaign(c.id)} title="Devam Et">
                                    <Play size={14} />
                                  </button>
                                )}
                                {c.status !== 'ended' && (
                                  <button className="btn btn-danger" onClick={() => showConfirmation({
                                    title: 'Kampanyayı Sonlandır',
                                    message: 'Bu kampanyayı sonlandırmak istediğinize emin misiniz? Fiyatlar orijinal haline döndürülecek.',
                                    type: 'danger',
                                    onConfirm: () => handleEndCampaign(c.id)
                                  })} title="Sonlandır" style={{ marginLeft: '6px' }}>
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {discountCampaigns.length === 0 && (
                        <div className="empty-state">Henüz kampanya yok</div>
                      )}
                    </div>
                  )}
                </>
              )}

              {discountSubTab === 'handles' && (
                <div className="settings-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Hedef</label>
                      <select value={handleScopeType} onChange={e => setHandleScopeType(e.target.value)}>
                        <option value="all">Tüm Ürünler</option>
                        <option value="category">Kategoriye Göre</option>
                        <option value="brand">Markaya Göre</option>
                        <option value="products">Seçili Ürünler</option>
                      </select>
                    </div>
                  </div>

                  {handleScopeType === 'category' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Kategori</label>
                        <select value={handleCategory} onChange={e => setHandleCategory(e.target.value)}>
                          <option value="">Seçin...</option>
                          {productCategories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                  )}

                  {handleScopeType === 'brand' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Marka</label>
                        <select value={handleBrand} onChange={e => setHandleBrand(e.target.value)}>
                          <option value="">Seçin...</option>
                          {productBrands.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </div>
                    </div>
                  )}

                  {handleScopeType === 'products' && (
                    <div className="form-row">
                      <div className="form-group" style={{ flex: 1 }}>
                        <label>Ürün Ara (isim / barkod)</label>
                        <input type="text" value={handleProductSearch}
                          onChange={e => setHandleProductSearch(e.target.value)}
                          placeholder="En az 2 karakter yazın..." />
                        {handleProductResults.length > 0 && (
                          <div className="product-picker-results">
                            {handleProductResults.map(p => (
                              <div key={p.id} className="product-picker-row"
                                onClick={() => {
                                  if (!handleSelectedProducts.find(sp => sp.id === p.id)) {
                                    setHandleSelectedProducts(prev => [...prev, { id: p.id, name: p.name, barcode: p.barcode }]);
                                  }
                                  setHandleProductSearch('');
                                  setHandleProductResults([]);
                                }}>
                                {p.name} {p.barcode ? `(${p.barcode})` : ''}
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                          {handleSelectedProducts.map(p => (
                            <span key={p.id} className="product-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'default' }}>
                              {p.name}
                              <X size={12} style={{ cursor: 'pointer' }}
                                onClick={() => setHandleSelectedProducts(prev => prev.filter(sp => sp.id !== p.id))} />
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="form-row" style={{ alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={handleRedirect} onChange={e => setHandleRedirect(e.target.checked)} />
                      Eski adresten yeniye otomatik yönlendirme oluştur
                    </label>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    <button className="btn" disabled={previewingHandle} onClick={handlePreviewHandles}>
                      <Search size={16} style={{ marginRight: '6px' }} />
                      {previewingHandle ? 'Hesaplanıyor...' : 'Önizle'}
                    </button>
                    <button className="btn btn-primary" disabled={applyingHandle} onClick={handleApplyHandles}>
                      {applyingHandle ? 'Uygulanıyor...' : 'Adresleri Güncelle'}
                    </button>
                  </div>

                  {handlePreview && (
                    <div className="table-container" style={{ marginTop: '16px' }}>
                      <p>
                        Hedefte <strong>{handlePreview.productCount}</strong> ürün var,
                        <strong> {handlePreview.changeCount}</strong> tanesinin adresi değişecek.
                        {handlePreview.conflicts > 0 && ` ${handlePreview.conflicts} ürün çakışma nedeniyle atlanacak.`}
                      </p>
                      <table className="data-table">
                        <thead>
                          <tr><th>Ürün</th><th>Yeni Adres</th><th>Durum</th></tr>
                        </thead>
                        <tbody>
                          {handlePreview.sample.map(item => (
                            <tr key={item.productId} style={item.conflict ? { color: '#ef4444' } : {}}>
                              <td>{item.title}</td>
                              <td>/products/{item.newHandle}</td>
                              <td>{item.conflict ? 'Çakışma - atlanacak' : 'Güncellenecek'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          </>}

          {/* ===== TRENDYOL CONTENT ===== */}
          {activePlatform === 'trendyol' && <>
          {/* Trendyol Settings Tab */}
          {activeTab === 'settings' && (
            <div className="shopify-panel">
              <h2>Trendyol Bağlantı Ayarları</h2>
              <div className="settings-form">
                {/* Connection Status */}
                <div className={`connection-card ${trendyolSettings.api_key && trendyolSettings.seller_id ? 'connected' : 'disconnected'}`}>
                  {trendyolSettings.api_key && trendyolSettings.seller_id ? (
                    <>
                      <div className="connection-info">
                        <span className="connection-icon">&#10003;</span>
                        <div>
                          <strong>Trendyol Bağlı</strong>
                          <p>Satıcı ID: {trendyolSettings.seller_id}</p>
                        </div>
                      </div>
                      <button className="btn btn-danger" onClick={() => showConfirmation({
                        title: 'Bağlantıyı Kes',
                        message: 'Trendyol bağlantısını kesmek istediğinize emin misiniz?',
                        type: 'danger',
                        onConfirm: disconnectTrendyol
                      })}>Bağlantıyı Kes</button>
                    </>
                  ) : (
                    <div className="connection-info">
                      <AlertTriangle size={24} color="#ef4444" />
                      <div>
                        <strong>Trendyol Bağlı Değil</strong>
                        <p>API bilgilerini girerek bağlayın</p>
                      </div>
                    </div>
                  )}
                </div>

                <hr className="settings-divider" />

                <div className="form-row">
                  <div className="form-group">
                    <label>Satıcı ID (Seller ID)</label>
                    <input type="text" value={trendyolSettings.seller_id}
                      onChange={e => setTrendyolSettings(prev => ({ ...prev, seller_id: e.target.value }))}
                      placeholder="154987" />
                  </div>
                  <div className="form-group">
                    <label>Entegrasyon Referans Kodu</label>
                    <input type="text" value={trendyolSettings.integration_code}
                      onChange={e => setTrendyolSettings(prev => ({ ...prev, integration_code: e.target.value }))}
                      placeholder="c17cc17e-..." />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>API Key</label>
                    <input type="password" value={trendyolSettings.api_key}
                      onChange={e => setTrendyolSettings(prev => ({ ...prev, api_key: e.target.value }))}
                      placeholder="API Key" />
                  </div>
                  <div className="form-group">
                    <label>API Secret</label>
                    <input type="password" value={trendyolSettings.api_secret}
                      onChange={e => setTrendyolSettings(prev => ({ ...prev, api_secret: e.target.value }))}
                      placeholder="API Secret" />
                  </div>
                </div>

                <hr className="settings-divider" />

                <div className="form-row">
                  <div className="form-group">
                    <label>Varsayılan Stok Düşüm Mağazası</label>
                    <select value={trendyolSettings.default_deduction_store_id}
                      onChange={e => setTrendyolSettings(prev => ({ ...prev, default_deduction_store_id: e.target.value }))}>
                      <option value="">Seçin...</option>
                      {stores.map(store => (
                        <option key={store.id} value={store.id}>{store.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Komisyon Yüzdesi (%)</label>
                    <input type="number" value={trendyolSettings.commission_percentage} step="0.01"
                      onChange={e => setTrendyolSettings(prev => ({ ...prev, commission_percentage: parseFloat(e.target.value) || 0 }))} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Fiyat Değişim Eşiği (%)</label>
                    <input type="number" value={trendyolSettings.price_change_threshold} step="0.1"
                      onChange={e => setTrendyolSettings(prev => ({ ...prev, price_change_threshold: parseFloat(e.target.value) || 2 }))} />
                  </div>
                  <div className="form-group">
                    <label>Otomatik Fiyat Sync Aralığı (dk, 0=kapalı)</label>
                    <input type="number" value={trendyolSettings.price_sync_interval} min="0"
                      onChange={e => setTrendyolSettings(prev => ({ ...prev, price_sync_interval: parseInt(e.target.value) || 0 }))} />
                  </div>
                </div>

                <div
                  onClick={() => setTrendyolSettings(prev => ({ ...prev, sync_enabled: !prev.sync_enabled }))}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '12px', marginTop: '8px',
                    background: trendyolSettings.sync_enabled ? 'rgba(16,185,129,0.1)' : '#f1f5f9',
                    padding: '10px 20px', borderRadius: '24px', cursor: 'pointer', transition: 'all 0.2s',
                    border: trendyolSettings.sync_enabled ? '2px solid #10b981' : '2px solid #e2e8f0'
                  }}
                >
                  <div style={{
                    width: '40px', height: '22px', borderRadius: '11px', position: 'relative', transition: 'all 0.3s',
                    background: trendyolSettings.sync_enabled ? '#10b981' : '#cbd5e1', flexShrink: 0
                  }}>
                    <div style={{
                      width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute',
                      top: '2px', left: trendyolSettings.sync_enabled ? '20px' : '2px', transition: 'all 0.3s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                    }} />
                  </div>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem', color: trendyolSettings.sync_enabled ? '#059669' : '#64748b' }}>
                    Senkronizasyon {trendyolSettings.sync_enabled ? 'Aktif' : 'Kapalı'}
                  </span>
                </div>

                {(trendyolSettings.last_synced_gold_price || trendyolSettings.last_price_sync_at) && (
                  <div style={{ marginTop: 16, padding: 12, background: '#f8fafc', borderRadius: 8, fontSize: '0.85rem', color: '#64748b' }}>
                    <div>Son sync altın: {trendyolSettings.last_synced_gold_price ? `${parseFloat(trendyolSettings.last_synced_gold_price).toFixed(2)} TL` : '-'}</div>
                    <div>Son sync USD: {trendyolSettings.last_synced_usd_rate ? `${parseFloat(trendyolSettings.last_synced_usd_rate).toFixed(4)}` : '-'}</div>
                    <div>Son fiyat sync: {trendyolSettings.last_price_sync_at ? new Date(trendyolSettings.last_price_sync_at).toLocaleString('tr-TR') : '-'}</div>
                  </div>
                )}

                <div className="form-actions" style={{ marginTop: '20px' }}>
                  <button className="btn btn-primary" onClick={saveTrendyolSettings} disabled={savingSettings}>
                    {savingSettings ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                  </button>
                  <button className="btn btn-secondary" onClick={testTrendyolConnection} disabled={testingConnection}>
                    {testingConnection ? 'Test ediliyor...' : 'Bağlantıyı Test Et'}
                  </button>
                </div>

                {trendyolConnectionStatus && (
                  <div style={{
                    marginTop: 12, padding: 12, borderRadius: 8,
                    background: trendyolConnectionStatus.success ? '#dcfce7' : '#fecaca',
                    color: trendyolConnectionStatus.success ? '#166534' : '#991b1b',
                    fontSize: '0.9rem'
                  }}>
                    {trendyolConnectionStatus.message}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Trendyol Products Tab */}
          {activeTab === 'products' && (
            <div className="shopify-panel">
              <div className="panel-header">
                <h2>Trendyol Ürünleri</h2>
                <div className="panel-actions">
                  {selectedTrendyolProducts.length > 0 && (
                    <button className="btn btn-danger" onClick={() => showConfirmation({
                      title: 'Toplu Silme', type: 'danger',
                      message: `${selectedTrendyolProducts.length} ürünü Trendyol'dan kaldırmak istediğinize emin misiniz?`,
                      onConfirm: bulkDeleteTrendyolProducts
                    })}>
                      <Trash2 size={16} /> Seçilenleri Sil ({selectedTrendyolProducts.length})
                    </button>
                  )}
                  <button className="btn-sm btn-primary" onClick={syncAllTrendyolProducts} disabled={syncingTrendyolProducts}>
                    {syncingTrendyolProducts ? 'Sync Ediliyor...' : <><Upload size={14} /> Tümünü Sync Et</>}
                  </button>
                  <button className="btn-sm btn-success" onClick={syncTrendyolInventory} disabled={syncingTrendyolInventory}>
                    {syncingTrendyolInventory ? 'Güncelleniyor...' : <><Package size={14} /> Stok Eşitle</>}
                  </button>
                  <button className="btn-sm btn-warning" onClick={syncTrendyolPrices} disabled={syncingTrendyolPrices}>
                    {syncingTrendyolPrices ? 'Güncelleniyor...' : <>Fiyat Eşitle</>}
                  </button>
                </div>
              </div>

              {/* Sync progress */}
              {trendyolSyncProgress && trendyolSyncProgress.running && (() => {
                const done = trendyolSyncProgress.success + trendyolSyncProgress.failed;
                const pct = trendyolSyncProgress.total > 0 ? Math.round((done / trendyolSyncProgress.total) * 100) : 0;
                return (
                  <div className="sync-progress-bar">
                    <div className="sync-progress-text">Sync: {done}/{trendyolSyncProgress.total} (%{pct})</div>
                    <div className="sync-progress-track">
                      <div className="sync-progress-fill" style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                );
              })()}

              <div className="products-table-container">
                {loadingTrendyol ? (
                  <div className="loading">Yükleniyor...</div>
                ) : (
                  <table className="products-table">
                    <thead>
                      <tr>
                        <th><input type="checkbox" checked={selectedTrendyolProducts.length === trendyolProducts.length && trendyolProducts.length > 0}
                          onChange={e => setSelectedTrendyolProducts(e.target.checked ? trendyolProducts.map(p => p.id) : [])} /></th>
                        <th className="photo-column">Resim</th>
                        <th>Ürün</th>
                        <th>Barkod</th>
                        <th>Kategori</th>
                        <th>Fiyat</th>
                        <th>Stok</th>
                        <th>Durum</th>
                        <th>Son Sync</th>
                        <th>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trendyolProducts.filter(p => {
                        if (!productFilter) return true;
                        const q = productFilter.toLowerCase();
                        return (p.name || '').toLowerCase().includes(q) || (p.barcode || '').toLowerCase().includes(q);
                      }).map(product => (
                        <tr key={product.id}>
                          <td><input type="checkbox" checked={selectedTrendyolProducts.includes(product.id)}
                            onChange={e => setSelectedTrendyolProducts(prev => e.target.checked ? [...prev, product.id] : prev.filter(id => id !== product.id))} /></td>
                          <td className="photo-cell">
                            {product.image ? (
                              <img className="product-photo" src={product.image.startsWith('http') ? product.image : `${API_URL.replace('/api', '')}${product.image.startsWith('/') ? '' : '/uploads/'}${product.image}`}
                                alt={product.name}
                                onClick={() => setImagePopup({ show: true, src: product.image.startsWith('http') ? product.image : `${API_URL.replace('/api', '')}${product.image.startsWith('/') ? '' : '/uploads/'}${product.image}`, name: product.name })}
                                onError={e => { e.target.style.display = 'none'; }} />
                            ) : <div className="no-photo"><Package size={20} /></div>}
                          </td>
                          <td className="product-name-cell">{product.name}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{product.barcode || '-'}</td>
                          <td>{product.category || '-'}</td>
                          <td style={{ fontWeight: 500 }}>{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(product.sell_price || 0)}</td>
                          <td>{product.total_stock || 0}</td>
                          <td><span className={`sync-badge ${product.sync_status || 'none'}`}>{product.sync_status || '-'}</span></td>
                          <td style={{ fontSize: '0.8rem' }}>{product.last_synced_at ? new Date(product.last_synced_at).toLocaleString('tr-TR') : '-'}</td>
                          <td>
                            <div className="action-buttons">
                              <button className="btn-icon btn-sync" onClick={() => syncSingleTrendyolProduct(product.id)} title="Sync Et">
                                <Upload size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {!loadingTrendyol && trendyolProducts.length === 0 && (
                  <div className="empty-state">E-ticaret aktif ürün bulunamadı</div>
                )}
              </div>
            </div>
          )}

          {/* Trendyol Orders Tab */}
          {activeTab === 'orders' && (
            <div className="shopify-panel">
              <div className="panel-header">
                <h2>Trendyol Siparişleri</h2>
                <div className="panel-actions">
                  {selectedTrendyolOrders.length > 0 && (
                    <>
                      <button className="btn btn-success" onClick={() => showConfirmation({
                        title: 'Toplu İşleme',
                        message: `${selectedTrendyolOrders.length} siparişi işlemek istediğinize emin misiniz?`,
                        onConfirm: bulkProcessTrendyolOrders
                      })}>
                        &#10003; Seçilenleri İşle ({selectedTrendyolOrders.length})
                      </button>
                      <button className="btn btn-danger" onClick={() => showConfirmation({
                        title: 'Sipariş Silme', type: 'danger',
                        message: `${selectedTrendyolOrders.length} siparişi silmek istediğinize emin misiniz?`,
                        onConfirm: bulkDeleteTrendyolOrders
                      })}>
                        <Trash2 size={16} /> Seçilenleri Sil ({selectedTrendyolOrders.length})
                      </button>
                    </>
                  )}
                  <button className="btn btn-success" onClick={() => showConfirmation({
                    title: 'Toplu İşleme',
                    message: 'Tüm bekleyen siparişleri işlemek istediğinize emin misiniz?',
                    onConfirm: bulkProcessTrendyolOrders
                  })}>
                    &#10003; Tümünü İşle
                  </button>
                  <button className="btn btn-danger" onClick={() => showConfirmation({
                    title: 'Sipariş Silme', type: 'danger',
                    message: 'Tüm siparişleri silmek istediğinize emin misiniz?',
                    onConfirm: bulkDeleteTrendyolOrders
                  })}>
                    <Trash2 size={16} /> Tüm Siparişleri Sil
                  </button>
                  <button className="btn btn-primary" onClick={pullTrendyolOrders} disabled={pullingTrendyolOrders}>
                    {pullingTrendyolOrders ? 'Çekiliyor...' : <><Download size={16} /> Siparişleri Çek</>}
                  </button>
                </div>
              </div>

              <div className="orders-filter-bar">
                <input type="text" placeholder="Sipariş ara..." value={orderSearchQuery} className="search-input"
                  onChange={e => setOrderSearchQuery(e.target.value)} />
                <select value={trendyolOrderFilter} onChange={e => setTrendyolOrderFilter(e.target.value)} className="filter-select">
                  <option value="all">Tümü</option>
                  <option value="pending">Bekleyen</option>
                  <option value="synced">İşlenmiş</option>
                  <option value="error">Hatalı</option>
                </select>
              </div>

              <div className="products-table-container">
                <table className="products-table">
                  <thead>
                    <tr>
                      <th><input type="checkbox" checked={selectedTrendyolOrders.length === trendyolOrders.length && trendyolOrders.length > 0}
                        onChange={e => setSelectedTrendyolOrders(e.target.checked ? trendyolOrders.map(o => o.id) : [])} /></th>
                      <th>Sipariş No</th>
                      <th>Müşteri</th>
                      <th>Tutar</th>
                      <th>Durum</th>
                      <th>Kargo</th>
                      <th>Tarih</th>
                      <th>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trendyolOrders.filter(o => {
                      if (!orderSearchQuery) return true;
                      const q = orderSearchQuery.toLowerCase();
                      return (o.trendyol_order_number || '').toLowerCase().includes(q) || (o.customer_name || '').toLowerCase().includes(q);
                    }).map(order => (
                      <tr key={order.id}>
                        <td><input type="checkbox" checked={selectedTrendyolOrders.includes(order.id)}
                          onChange={e => setSelectedTrendyolOrders(prev => e.target.checked ? [...prev, order.id] : prev.filter(id => id !== order.id))} /></td>
                        <td style={{ fontWeight: 600, color: '#f97316', cursor: 'pointer' }}
                          onClick={() => setSelectedOrder(order)}>
                          #{order.trendyol_order_number}
                        </td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{order.customer_name || '-'}</div>
                          <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{order.customer_phone || ''}</div>
                        </td>
                        <td style={{ fontWeight: 600 }}>{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(order.total_price || 0)}</td>
                        <td><span className={`sync-badge ${order.sync_status}`}>{order.sync_status}</span></td>
                        <td style={{ fontSize: '0.85rem' }}>{order.cargo_provider || '-'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{order.trendyol_created_at ? new Date(order.trendyol_created_at).toLocaleString('tr-TR') : '-'}</td>
                        <td>
                          {order.sync_status !== 'synced' && (
                            <button className="btn btn-small btn-primary" onClick={() => processTrendyolOrder(order.id)}>İşle</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {trendyolOrders.length === 0 && (
                  <div className="empty-state">Sipariş bulunamadı</div>
                )}
              </div>
            </div>
          )}

          {/* Trendyol Customers Tab */}
          {activeTab === 'customers' && (
            <div className="shopify-panel">
              <div className="panel-header">
                <h2>Trendyol Müşterileri</h2>
                <div className="panel-actions">
                  <button className="btn btn-secondary" onClick={fetchTrendyolCustomers}>
                    <RefreshCw size={16} /> Yenile
                  </button>
                </div>
              </div>
              <div className="products-table-container">
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>Müşteri</th>
                      <th>Email</th>
                      <th>Telefon</th>
                      <th>Sipariş</th>
                      <th>Toplam Harcama</th>
                      <th>Son Sipariş</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trendyolCustomers.filter(c => {
                      if (!customerSearchQuery) return true;
                      const q = customerSearchQuery.toLowerCase();
                      return (c.full_name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
                    }).map(customer => (
                      <tr key={customer.id}>
                        <td style={{ fontWeight: 600 }}>{customer.full_name}</td>
                        <td>{customer.email || '-'}</td>
                        <td>{customer.phone || '-'}</td>
                        <td>{customer.orders_count}</td>
                        <td style={{ fontWeight: 600 }}>{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(customer.total_spent || 0)}</td>
                        <td style={{ fontSize: '0.85rem' }}>{customer.last_order_date ? new Date(customer.last_order_date).toLocaleString('tr-TR') : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {trendyolCustomers.length === 0 && (
                  <div className="empty-state">Müşteri bulunamadı</div>
                )}
              </div>
            </div>
          )}

          {/* Trendyol Logs Tab */}
          {activeTab === 'logs' && (
            <div className="shopify-panel">
              <h2>Trendyol Sync Logları</h2>
              <div className="orders-filter-bar">
                <select value={logFilter.type} onChange={e => setLogFilter(prev => ({ ...prev, type: e.target.value }))} className="filter-select">
                  <option value="">Tüm Tipler</option>
                  <option value="product_push">Ürün Push</option>
                  <option value="product_update">Ürün Güncelleme</option>
                  <option value="inventory_update">Stok Güncelleme</option>
                  <option value="order_pull">Sipariş Çekme</option>
                  <option value="price_update">Fiyat Güncelleme</option>
                  <option value="product_delete">Ürün Silme</option>
                </select>
                <select value={logFilter.status} onChange={e => setLogFilter(prev => ({ ...prev, status: e.target.value }))} className="filter-select">
                  <option value="">Tüm Durumlar</option>
                  <option value="success">Başarılı</option>
                  <option value="error">Hata</option>
                  <option value="warning">Uyarı</option>
                </select>
                <button className="btn btn-secondary" onClick={fetchTrendyolLogs}><RefreshCw size={16} /> Yenile</button>
              </div>
              <div className="products-table-container">
                <table className="products-table logs-table">
                  <thead>
                    <tr>
                      <th>Tarih</th>
                      <th>Tip</th>
                      <th>Durum</th>
                      <th>Mesaj</th>
                      <th>Entity ID</th>
                      <th>Trendyol ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trendyolLogs.map(log => (
                      <tr key={log.id}>
                        <td className="log-date">{new Date(log.created_at).toLocaleString('tr-TR')}</td>
                        <td><span className="log-type">{log.sync_type}</span></td>
                        <td><span className={`log-status ${log.status}`}>{log.status}</span></td>
                        <td className="log-message">{log.message}</td>
                        <td>{log.entity_id || '-'}</td>
                        <td>{log.trendyol_id || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {trendyolLogs.length === 0 && (
                  <div className="empty-state">Log bulunamadı</div>
                )}
              </div>
            </div>
          )}
          </>}
        </main>
      </div>

      {/* Image Popup Modal */}
      {imagePopup.show && (
        <div className="image-popup-overlay" onClick={() => setImagePopup({ show: false, src: '', name: '' })}>
          <div className="image-popup-content" onClick={e => e.stopPropagation()}>
            <button className="image-popup-close" onClick={() => setImagePopup({ show: false, src: '', name: '' })}>✕</button>
            <img src={imagePopup.src} alt={imagePopup.name} className="image-popup-img" />
            <div className="image-popup-name">{imagePopup.name}</div>
          </div>
        </div>
      )}

      {/* Genel Onay Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.2s ease'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '400px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            animation: 'slideUp 0.3s ease'
          }}>
            <div style={{
              background: confirmModalConfig.type === 'danger'
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                : confirmModalConfig.type === 'warning'
                  ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                  : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={24} color="white" />
              <h3 style={{
                margin: 0,
                color: 'white',
                fontSize: '18px',
                fontWeight: '600'
              }}>{confirmModalConfig.title}</h3>
            </div>
            <div style={{ padding: '24px' }}>
              <p style={{
                margin: '0 0 24px 0',
                color: '#4b5563',
                fontSize: '15px',
                lineHeight: '1.6'
              }}>{confirmModalConfig.message}</p>
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #e5e7eb',
                    background: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    transition: 'all 0.2s'
                  }}
                >
                  {confirmModalConfig.cancelText}
                </button>
                <button
                  onClick={() => {
                    confirmModalConfig.onConfirm();
                    setShowConfirmModal(false);
                  }}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    background: confirmModalConfig.type === 'danger'
                      ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                      : confirmModalConfig.type === 'warning'
                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                        : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                >
                  {confirmModalConfig.confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopifySettings;
